/* eslint-disable array-callback-return */
/* eslint-disable no-useless-escape */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import InlineCheckbox from "./InlineCheckbox";
import Checkbox from "./Checkbox";
import SelectInput from "./SelectInput";
import TextInput from "./TextInput";
import AddInput from "./AddInput";
import api from "../api";
import CustomCombobox from "./CustomCombobox";
import Toggle from "./Toggle";
import SimpleCombobox from "./SimpleCombobox";
import PdfIcon from "../assets/PDFIcon";
import { Dialog } from "@headlessui/react";
import Modal from "./Modal";
import TextEditor from "./TextEditor";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, TimeField } from "@mui/x-date-pickers";
import { formatDateDDMMYYYY } from "../util/formatDate";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

function TabSection({ title, subtitle, children, className = "" }) {
    return (
        <section
            className={classNames(
                "rounded-3xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 sm:p-5",
                className,
            )}
        >
            {(title || subtitle) && (
                <div className="mb-4">
                    {title && (
                        <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                            {title}
                        </h2>
                    )}
                    {subtitle && (
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {subtitle}
                        </p>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

function TabFooter({ leftAction, rightAction }) {
    const flattenChildren = (children) =>
        React.Children.toArray(children).flatMap((child) => {
            if (!child) return [];
            if (React.isValidElement(child) && child.type === React.Fragment) {
                return flattenChildren(child.props.children);
            }
            return [child];
        });

    const rightItems = flattenChildren(rightAction).filter(Boolean);
    const primaryRightAction =
        rightItems.length > 0 ? rightItems[rightItems.length - 1] : null;
    const secondaryRightActions =
        rightItems.length > 1 ? rightItems.slice(0, -1) : [];

    return (
        <div className="mt-6 border-t border-slate-200 bg-slate-50/60 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/60 sm:px-6">
            <div className="flex flex-col gap-3 sm:hidden">
                {secondaryRightActions.length > 0 && (
                    <div className="flex flex-col gap-3">{secondaryRightActions}</div>
                )}
                {(leftAction || primaryRightAction) && (
                    <div className="grid grid-cols-2 gap-3">
                        {leftAction ? leftAction : <div />}
                        {primaryRightAction ? primaryRightAction : <div />}
                    </div>
                )}
            </div>
            <div className="hidden sm:flex sm:flex-row sm:justify-between sm:gap-3">
                {leftAction ? <div>{leftAction}</div> : <div />}
                {rightItems.length > 0 ? (
                    <div className="flex flex-col gap-3 sm:flex-row">{rightItems}</div>
                ) : null}
            </div>
        </div>
    );
}

function RefractionRecordSection({ title, rightValues, leftValues }) {
    return (
        <div>
            <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                {title}
            </h1>

            <div className="mt-4 space-y-3 lg:hidden">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900">
                    <p className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        OD
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        {rightValues.map((field) => (
                            <div key={field.name} className="rounded-xl bg-white p-3 shadow-sm dark:bg-slate-950">
                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    {field.label}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                                    {field.value || "--"}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900">
                    <p className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        OI
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        {leftValues.map((field) => (
                            <div key={field.name} className="rounded-xl bg-white p-3 shadow-sm dark:bg-slate-950">
                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    {field.label}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                                    {field.value || "--"}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-4 hidden overflow-x-auto lg:block">
                <div className="grid min-w-[640px] grid-cols-6 gap-4">
                    <p className="mt-7 text-end">OD</p>
                    {rightValues.map((field) => (
                        <TextInput
                            key={field.name}
                            value={field.value || "--"}
                            disabled={true}
                            name={field.name}
                            label={field.label}
                            labelClassName="w-full text-center block"
                        />
                    ))}
                    <p className="mt-2 text-end">OI</p>
                    {leftValues.map((field) => (
                        <TextInput
                            key={field.name}
                            value={field.value || "--"}
                            disabled={true}
                            name={field.name}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

const visionValues = [
    { name: "20/10", id: 1 },
    { name: "20/15", id: 2 },
    { name: "20/20", id: 3 },
    { name: "20/25", id: 4 },
    { name: "20/30", id: 5 },
    { name: "20/40", id: 6 },
    { name: "20/50", id: 7 },
    { name: "20/60", id: 8 },
    { name: "20/70", id: 9 },
    { name: "20/80", id: 10 },
    { name: "20/100", id: 11 },
    { name: "20/150", id: 12 },
    { name: "20/200", id: 13 },
    { name: "20/400", id: 14 },
    { name: "CD", id: 15 },
    { name: "MM", id: 16 },
    { name: "SPL", id: 17 },
    { name: "BPL", id: 18 },
    { name: "MPL", id: 19 },
    { name: "NPL", id: 20 },
];

const General = function (props) {
    const [diagnostics, setDiagnostics] = useState([]);
    const [firstRender, setFirstRender] = useState(false);
    const [general, setGeneral] = useState({
        diagnoses: undefined,
        motiveOfConsultation: "",
    });
    // eslint-disable-next-line no-unused-vars
    const [time, setTime] = useState(undefined);
    const [diagnosticCreateError, setDiagnosticCreateError] = useState("");

    const goForward = function (props) {
        let error = false;
        if (props.consultation.attentionDate === "") {
            props.handleError("attentionDate", "Este campo es obligatorio");
            error = true;
        }

        if (!error) {
            props.setTab("Visión y Tensión");
        }
    };

    useEffect(() => {
        if (
            props.idConsultation &&
            !props.consultation?.general?.motiveOfConsultation
        ) {
        } else if (props.consultation.general && !firstRender) {
            setGeneral(props.consultation.general);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, props.idConsultation, firstRender]);

    const [diagnosticsQuery, setDiagnosticsQuery] = useState("");

    useEffect(() => {
        const handle = setTimeout(() => {
            api.diagnostics
                .getMulti(null, diagnosticsQuery || null)
                .then((response) => {
                    setDiagnostics(
                        Array.isArray(response)
                            ? response
                            : Array.isArray(response?.results)
                                ? response.results
                                : [],
                    );
                })
                .catch(() => setDiagnostics([]));
        }, 300);
        return () => clearTimeout(handle);
    }, [diagnosticsQuery]);

    useEffect(() => {
        props.handleChange("general", general);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [general]);

    useEffect(() => {
        if (props.errors) console.log(props.errors);
    }, [props.errors]);

    const handleGeneral = (field, value) =>
        setGeneral((prev) => ({ ...prev, [field]: value }));

    useEffect(() => {
        if (time) handleGeneral("sampleTime", time);
    }, [time]);

    // function hasMatchingAttribute(obj, names) {
    //     return names.some((name) => obj.hasOwnProperty(name));
    // }

    const createDiagnostic = async (name) => {
        try {
            setDiagnosticCreateError("");
            let response = await api.diagnostics.create(name);
            return response;
        } catch (e) {
            const msg =
                e?.message?.name?.[0] ||
                e?.message?.detail ||
                "No se pudo crear el diagnóstico";
            setDiagnosticCreateError(msg);
            return Promise.reject(e);
        }
    };

    const getLastConsultationWithGeneral = (history) => {
        const list = history?.consultationList ?? [];
        return list
            .filter((c) => c?.general)
            .reduce((best, cur) => {
                if (!best) return cur;
                if (cur.consultationDate > best.consultationDate) return cur;
                if (cur.consultationDate < best.consultationDate) return best;
                return (cur.id ?? 0) > (best.id ?? 0) ? cur : best;
            }, null);
    };

    const autoComplete = () => {
        const last = getLastConsultationWithGeneral(props.history);
        handleGeneral(
            "motiveOfConsultation",
            last.general.motiveOfConsultation,
        );
        handleGeneral("annotations", last.general.annotations);
        props.handleChange("managementPlan", last.managementPlan);
    };

    return (
        <div>
            <div className="space-y-5 px-4 py-5 sm:p-4">
                <TabSection
                    title="Datos generales"
                >
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
                        <TextEditor
                            required={true}
                            rows={8}
                            value={general?.motiveOfConsultation}
                            error={props.errors?.motiveOfConsultation}
                            handleChange={handleGeneral}
                            containerClassName="lg:col-span-2"
                            name="motiveOfConsultation"
                            label="Motivo de consulta"
                        />
                        <CustomCombobox
                            create={createDiagnostic}
                            multiple={true}
                            required={true}
                            value={general?.diagnoses}
                            error={diagnosticCreateError || props.errors?.diagnoses}
                            handleChange={handleGeneral}
                            options={diagnostics}
                            onQueryChange={setDiagnosticsQuery}
                            name="diagnoses"
                            containerClassName="relative w-full"
                            label="Diagnóstico"
                        />
                        <TextEditor
                            error={props.errors?.annotations}
                            rows={8}
                            value={general?.annotations}
                            handleChange={handleGeneral}
                            name="annotations"
                            label="Hallazgos clínicos"
                            containerClassName="lg:col-span-3"
                        />
                        <TextEditor
                            error={props.errors?.managementPlan}
                            rows={8}
                            value={general?.managementPlan}
                            handleChange={handleGeneral}
                            name="managementPlan"
                            label="Conducta"
                            containerClassName="lg:col-span-3"
                        />
                    </div>
                </TabSection>
            </div>
            <TabFooter
                rightAction={
                    <>
                        <button
                            type="button"
                            onClick={() => autoComplete()}
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-primary bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:bg-white dark:bg-gray-900"
                        >
                            Completar con ultima consulta
                        </button>
                        <button
                            onClick={() => goForward(props)}
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                        >
                            Siguiente
                        </button>
                    </>
                }
            />
        </div>
    );
};

const VisionTension = function (props) {
    const [firstRender, setFirstRender] = useState(false);
    const [visionTension, setVisionTension] = useState({
        noTensionMeasurement: false,
        noVisionMeasurement: false,
    });
    const [time, setTime] = useState(undefined);

    const goForward = function (props) {
        let error = false;
        if (props.consultation.attentionDate === "") {
            props.handleError("attentionDate", "Este campo es obligatorio");
            error = true;
        }

        if (!error) {
            props.setTab("Refracción");
        }
    };

    useEffect(() => {
        if (props.consultation.visionTension && !firstRender) {
            setVisionTension(props.consultation.visionTension);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, props.idConsultation, firstRender]);

    useEffect(() => {
        props.handleChange("visionTension", visionTension);
    }, [visionTension]);

    useEffect(() => {
        if (!visionTension.tensionLeft) delete visionTension.tensionLeft;
    }, [visionTension.tensionLeft]);

    useEffect(() => {
        if (!visionTension.tensionRight) delete visionTension.tensionRight;
    }, [visionTension.tensionRight]);

    useEffect(() => {
        if (!visionTension.sampleTime) delete visionTension.sampleTime;
    }, [visionTension.sampleTime]);

    useEffect(() => {
        if (props.errors) console.log(props.errors);
    }, [props.errors]);

    const handleVision = (field, value) =>
        setVisionTension((prev) => ({ ...prev, [field]: value }));

    useEffect(() => {
        if (time) handleVision("sampleTime", time);
    }, [time]);

    const getSampleTimeDate = () => {
        if (!visionTension?.sampleTime) return null;
        const parts = String(visionTension.sampleTime).split(":");
        const hh = parseInt(parts?.[0], 10);
        const mm = parseInt(parts?.[1], 10);
        if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
        const d = new Date();
        d.setHours(hh, mm, 0, 0);
        return d;
    };

    const isDark = document.documentElement.classList.contains("dark");

    return (
        <div>
            <div className="space-y-5 px-4 py-5 text-black dark:text-white sm:p-4">
                <TabSection
                    title="Vision"
                >
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:divide-x-2">
                        <div className="lg:pr-4">
                            <p className="dark:text-white">Sin corrección</p>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <SelectInput
                                    placeholder={true}
                                    value={
                                        visionTension?.visionWithoutCorrectionRight
                                    }
                                    options={visionValues.map((element) => (
                                        <option
                                            key={element.id}
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                    className="input-base"
                                    name="visionWithoutCorrectionRight"
                                    handleChange={handleVision}
                                    label="OD"
                                    labelClassName="w-full text-center block"
                                />
                                <SelectInput
                                    placeholder={true}
                                    value={
                                        visionTension?.visionWithoutCorrectionLeft
                                    }
                                    options={visionValues.map((element) => (
                                        <option
                                            key={element.id}
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                    className="input-base"
                                    name="visionWithoutCorrectionLeft"
                                    handleChange={handleVision}
                                    label="OI"
                                    labelClassName="w-full text-center block"
                                />
                            </div>
                        </div>
                        <div className="lg:px-4">
                            <p className="dark:text-white">Con corrección</p>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <SelectInput
                                    placeholder={true}
                                    value={
                                        visionTension?.visionWithCorrectionRight
                                    }
                                    options={visionValues.map((element) => (
                                        <option
                                            key={element.id}
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                    className="input-base"
                                    name="visionWithCorrectionRight"
                                    handleChange={handleVision}
                                    label="OD"
                                    labelClassName="w-full text-center block"
                                />
                                <SelectInput
                                    placeholder={true}
                                    value={
                                        visionTension?.visionWithCorrectionLeft
                                    }
                                    containerClassName="flex w-full justify-center"
                                    options={visionValues.map((element) => (
                                        <option
                                            key={element.id}
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                    className="input-base"
                                    name="visionWithCorrectionLeft"
                                    handleChange={handleVision}
                                    label="OI"
                                    labelClassName="w-full text-center block"
                                />
                            </div>
                        </div>
                        <div className="lg:pl-4">
                            <p className="dark:text-white">Con estenopeico</p>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <SelectInput
                                    placeholder={true}
                                    value={
                                        visionTension?.visionWithStenopeicRight
                                    }
                                    options={visionValues.map((element) => (
                                        <option
                                            key={element.id}
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                    className="input-base"
                                    name="visionWithStenopeicRight"
                                    handleChange={handleVision}
                                    label="OD"
                                    labelClassName="w-full text-center block"
                                />
                                <SelectInput
                                    placeholder={true}
                                    value={
                                        visionTension?.visionWithStenopeicLeft
                                    }
                                    options={visionValues.map((element) => (
                                        <option
                                            key={element.id}
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                    className="input-base"
                                    name="visionWithStenopeicLeft"
                                    handleChange={handleVision}
                                    label="OI"
                                    labelClassName="w-full text-center block"
                                />
                            </div>
                        </div>
                    </div>
                    <InlineCheckbox
                        value={visionTension?.noVisionMeasurement}
                        name="noVisionMeasurement"
                        handleChange={handleVision}
                        containerClassName="mt-4 flex"
                        label="No fue necesaria una medición"
                    />
                    {props.visionFieldsErrors && (
                        <p className="mt-2 text-sm text-red-600" id={`${props.name}-error`}>
                            {props.visionFieldsErrors}
                        </p>
                    )}
                </TabSection>

                <TabSection
                    title="Tension"
                >
                    <div className="grid grid-cols-1 gap-x-3 gap-y-6 md:grid-cols-2 xl:grid-cols-4">
                        <TextInput
                            onKeyDown={(event) => {
                                if (
                                    /[0-9]/.test(event.key) ||
                                    event.key === "Backspace" ||
                                    event.key === "Tab"
                                ) {
                                } else {
                                    event.preventDefault();
                                }
                            }}
                            value={visionTension?.tensionRight}
                            description="0 a 100"
                            name="tensionRight"
                            handleChange={handleVision}
                            label="OD"
                            labelClassName="w-full text-center block"
                        />
                        <TextInput
                            onKeyDown={(event) => {
                                if (
                                    /[0-9]/.test(event.key) ||
                                    event.key === "Backspace" ||
                                    event.key === "Tab"
                                ) {
                                } else {
                                    event.preventDefault();
                                }
                            }}
                            value={visionTension?.tensionLeft}
                            description="0 a 100"
                            name="tensionLeft"
                            handleChange={handleVision}
                            label="OI"
                            labelClassName="w-full text-center block"
                        />
                        <div className="md:col-span-2 xl:col-span-1">
                            <label className="whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-200">
                                Hora de muestra
                            </label>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <TimeField
                                    value={getSampleTimeDate()}
                                    format="HH:mm"
                                    onChange={(value) => {
                                        if (!value) {
                                            setTime(undefined);
                                            return;
                                        }
                                        setTime(
                                            `${value
                                                .getHours()
                                                .toString()
                                                .padStart(2, "0")}:${value
                                                .getMinutes()
                                                .toString()
                                                .padStart(2, "0")}`,
                                        );
                                    }}
                                    size="small"
                                    sx={
                                        isDark
                                            ? {
                                                  "& .MuiInputBase-root": {
                                                      backgroundColor: "rgb(17 24 39)",
                                                      color: "rgb(229 231 235)",
                                                  },
                                                  "& .MuiOutlinedInput-notchedOutline": {
                                                      borderColor: "rgb(75 85 99)",
                                                  },
                                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                                      borderColor: "rgb(107 114 128)",
                                                  },
                                                  "& .MuiSvgIcon-root": {
                                                      color: "rgb(229 231 235)",
                                                  },
                                              }
                                            : undefined
                                    }
                                />
                            </LocalizationProvider>
                        </div>
                        <InlineCheckbox
                            value={visionTension?.noTensionMeasurement}
                            name="noTensionMeasurement"
                            handleChange={handleVision}
                            containerClassName="flex md:col-span-2 xl:col-span-4"
                            label="No fue necesaria una medición"
                        />
                        <div className="text-xs font-medium text-[#F51515] md:col-span-2 xl:col-span-4">
                            <p>{props.errorTensionRight}</p>
                            <p>{!props.errorTensionRight && props.errorTensionLeft}</p>
                            <p>
                                {!props.errorTensionRight &&
                                    !props.errorTensionLeft &&
                                    props.errorSampleTime}
                            </p>
                            <p>{props.errors?.tensionLeft}</p>
                            <p>{props.errors?.tensionRight}</p>
                            <p>{props.errors?.sampleTime}</p>
                            <p>{props.errors?.nonFieldErrors}</p>
                        </div>
                    </div>
                </TabSection>
            </div>
            <TabFooter
                leftAction={
                    <button
                        onClick={() => props.setTab("General")}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:text-primary dark:bg-gray-900"
                    >
                        Anterior
                    </button>
                }
                rightAction={
                    <button
                        onClick={() => goForward(props)}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                    >
                        Siguiente
                    </button>
                }
            />
        </div>
    );
};

const Refraction = function (props) {
    const [generatePrescription, setGeneratePrescription] = useState(false);
    const [refraction, setRefraction] = useState({});
    const [prescription, setPrescription] = useState({});
    const [refractionRecordsOpen, setRefractionRecordsOpen] = useState(false);
    const [aux, setAux] = useState(undefined);
    const [firstRender, setFirstRender] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadError, setDownloadError] = useState(undefined);
    const [selectedDate, setSelectedDate] = useState(undefined);

    useEffect(() => {
        if (props.consultation.refraction && !firstRender) {
            setRefraction(props.consultation.refraction);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, firstRender]);

    const handleRefraction = (field, value) =>
        setRefraction((prev) => ({ ...prev, [field]: value }));
    const handlePrescription = (field, value) =>
        setPrescription((prev) => ({ ...prev, [field]: value }));
    const handleAux = (field, value) => setAux(value);

    useEffect(() => {
        if (generatePrescription) {
            handlePrescription(
                "farVisionSphericalRight",
                refraction.farVisionSphericalRight,
            );
            handlePrescription(
                "farVisionCylindricalRight",
                refraction.farVisionCylindricalRight,
            );
            handlePrescription(
                "farVisionAxisRight",
                refraction.farVisionAxisRight,
            );
            handlePrescription("additionRight", refraction.additionRight);
            handlePrescription(
                "farVisionSphericalLeft",
                refraction.farVisionSphericalLeft,
            );
            handlePrescription(
                "farVisionCylindricalLeft",
                refraction.farVisionCylindricalLeft,
            );
            handlePrescription(
                "farVisionAxisLeft",
                refraction.farVisionAxisLeft,
            );
            handlePrescription("additionLeft", refraction.additionLeft);
            handlePrescription(
                "nearVisionSphericalRight",
                refraction.nearVisionSphericalRight,
            );
            handlePrescription(
                "nearVisionCylindricalRight",
                refraction.nearVisionCylindricalRight,
            );
            handlePrescription(
                "nearVisionAxisRight",
                refraction.nearVisionAxisRight,
            );
            handlePrescription(
                "nearVisionSphericalLeft",
                refraction.nearVisionSphericalLeft,
            );
            handlePrescription(
                "nearVisionCylindricalLeft",
                refraction.nearVisionCylindricalLeft,
            );
            handlePrescription(
                "nearVisionAxisLeft",
                refraction.nearVisionAxisLeft,
            );
            handleRefraction("eyeglassPrescription", prescription);
        } else {
            setRefraction((prev) => {
                const next = { ...prev };
                delete next.eyeglassPrescription;
                return next;
            });
        }
    }, [generatePrescription]);

    useEffect(() => {
        if (generatePrescription)
            handleRefraction("eyeglassPrescription", prescription);
    }, [prescription]);

    useEffect(() => {
        props.handleChange("refraction", refraction);
    }, [refraction]);

    const downloadPDF = function () {
        setLoadingDownload(true);
        setDownloadError("");
        api.refraction
            .get({
                patient: props.consultation.patient,
                doctor: props.consultation.doctor,
                generalObservations:
                    props.consultation.refraction.generalObservations,
                eyeglassPrescription:
                    props.consultation.refraction.eyeglassPrescription,
                printWithInsurance: props.printWithInsurance,
            })
            .then(
                (res) => {
                    setLoadingDownload(false);
                    const url = window.URL.createObjectURL(new Blob([res]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "Refraccion.pdf");
                    document.body.appendChild(link);
                    link.click();
                },
                async (err) => {
                    setDownloadError(
                        JSON.parse(await err.response.data.text()).detail,
                    );
                    setLoadingDownload(false);
                },
            );
    };

    useEffect(() => {
        if (!refractionRecordsOpen) {
            setSelectedDate(undefined);
            setAux(undefined);
        }
    }, [refractionRecordsOpen]);
    const refractionRecords = (
        <>
            {props.refractionRecords && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div
                        className={classNames(
                            selectedDate ? "lg:col-span-1" : "lg:col-span-3",
                            "mt-3 text-center sm:mt-5",
                        )}
                    >
                        <Dialog.Title
                            as="h3"
                            className="leading-6 text-[#75C9EE] font-semibold text-2xl"
                        >
                            Consultas anteriores
                        </Dialog.Title>
                        <div className="mt-8">
                            <SelectInput
                                containerStyle="mx-auto"
                                placeholder={true}
                                placeholderText="Seleccionar fecha"
                                value={aux}
                                options={props.refractionRecords.map(
                                    (element, idx) => (
                                        <option
                                            key={idx}
                                            value={idx + 1}
                                            label={formatDateDDMMYYYY(element.consultationDate)}
                                        >
                                            {formatDateDDMMYYYY(element.consultationDate)}
                                        </option>
                                    ),
                                )}
                                name="aux"
                                handleChange={handleAux}
                                label="Selecciona una fecha"
                            />
                            <button
                                type="button"
                                onClick={() => setSelectedDate(aux)}
                                className="w-full mt-10 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                            >
                                Buscar
                            </button>
                            <button
                                type="button"
                                onClick={() => setRefractionRecordsOpen(false)}
                                className="w-full mt-4 inline-flex justify-center rounded-md border shadow-sm px-4 py-2 bg-white text-base font-medium text-black sm:col-start-2 sm:text-sm"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                    {selectedDate && (
                        <div className="flex flex-col gap-3 lg:col-span-2">
                            <RefractionRecordSection
                                title="Visión lejana"
                                rightValues={[
                                    {
                                        name: "farVisionSphericalRight",
                                        label: "Esf",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.farVisionSphericalRight,
                                    },
                                    {
                                        name: "farVisionCylindricalRight",
                                        label: "Cil.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.farVisionCylindricalRight,
                                    },
                                    {
                                        name: "farVisionAxisRight",
                                        label: "Eje",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction?.farVisionAxisRight,
                                    },
                                    {
                                        name: "farVisionVisualAcuityRight",
                                        label: "Av.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.farVisionVisualAcuityRight,
                                    },
                                ]}
                                leftValues={[
                                    {
                                        name: "farVisionSphericalLeft",
                                        label: "Esf",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction?.farVisionSphericalLeft,
                                    },
                                    {
                                        name: "farVisionCylindricalLeft",
                                        label: "Cil.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.farVisionCylindricalLeft,
                                    },
                                    {
                                        name: "farVisionAxisLeft",
                                        label: "Eje",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction?.farVisionAxisLeft,
                                    },
                                    {
                                        name: "farVisionVisualAcuityLeft",
                                        label: "Av.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.farVisionVisualAcuityLeft,
                                    },
                                ]}
                            />
                            <RefractionRecordSection
                                title="Visión cercana"
                                rightValues={[
                                    {
                                        name: "nearVisionSphericalRight",
                                        label: "Esf",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.nearVisionSphericalRight,
                                    },
                                    {
                                        name: "nearVisionCylindricalRight",
                                        label: "Cil.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.nearVisionCylindricalRight,
                                    },
                                    {
                                        name: "nearVisionAxisRight",
                                        label: "Eje",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction?.nearVisionAxisRight,
                                    },
                                    {
                                        name: "nearVisionVisualAcuityRight",
                                        label: "Av.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.nearVisionVisualAcuityRight,
                                    },
                                ]}
                                leftValues={[
                                    {
                                        name: "nearVisionSphericalLeft",
                                        label: "Esf",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.nearVisionSphericalLeft,
                                    },
                                    {
                                        name: "nearVisionCylindricalLeft",
                                        label: "Cil.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.nearVisionCylindricalLeft,
                                    },
                                    {
                                        name: "nearVisionAxisLeft",
                                        label: "Eje",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction?.nearVisionAxisLeft,
                                    },
                                    {
                                        name: "nearVisionVisualAcuityLeft",
                                        label: "Av.",
                                        value:
                                            props.refractionRecords[
                                                selectedDate - 1
                                            ]?.refraction
                                                ?.nearVisionVisualAcuityLeft,
                                    },
                                ]}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div>
            <Modal
                size={
                    selectedDate ? "w-full sm:max-w-6xl" : "w-full sm:max-w-2xl"
                }
                content={refractionRecords}
                open={refractionRecordsOpen}
                setOpen={setRefractionRecordsOpen}
            />
            <div className="space-y-5 px-4 py-6 sm:p-4">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
                    <TabSection title="Refracción">
                        <div className="flex flex-col gap-6">
                            <div>
                                <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                    Visión lejana
                                </h1>
                                <div className="grid grid-cols-5 gap-4 w-10/12 sm:w-full mt-4">
                                    <p className="text-end mt-7 dark:text-white">
                                        OD
                                    </p>
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.farVisionSphericalRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.farVisionSphericalRight
                                        }
                                        handleChange={handleRefraction}
                                        name="farVisionSphericalRight"
                                        label="Esf"
                                        labelClassName="w-full text-center block"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.farVisionCylindricalRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.farVisionCylindricalRight
                                        }
                                        handleChange={handleRefraction}
                                        name="farVisionCylindricalRight"
                                        label="Cil."
                                        labelClassName="w-full text-center block"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.farVisionAxisRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={refraction?.farVisionAxisRight}
                                        handleChange={handleRefraction}
                                        name="farVisionAxisRight"
                                        label="Eje"
                                        labelClassName="w-full text-center block"
                                    />
                                    <SelectInput
                                        errorMessage={
                                            props.errors
                                                ?.farVisionVisualAcuityRight
                                        }
                                        containerStyle="-mt-1"
                                        className="shadow-sm w-fit mt-1 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                        placeholder={true}
                                        value={
                                            refraction?.farVisionVisualAcuityRight
                                        }
                                        options={visionValues.map((element) => (
                                            <option
                                                key={element.id}
                                                value={element.id}
                                                label={element.name}
                                            >
                                                {element.name}
                                            </option>
                                        ))}
                                        name="farVisionVisualAcuityRight"
                                        handleChange={handleRefraction}
                                        label="Av."
                                        labelClassName="w-full text-center block dark:text-gray-200"
                                    />
                                    <p className="text-end mt-2 dark:text-white">
                                        OI
                                    </p>
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.farVisionSphericalLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.farVisionSphericalLeft
                                        }
                                        handleChange={handleRefraction}
                                        name="farVisionSphericalLeft"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.farVisionCylindricalLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.farVisionCylindricalLeft
                                        }
                                        handleChange={handleRefraction}
                                        name="farVisionCylindricalLeft"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.farVisionAxisLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={refraction?.farVisionAxisLeft}
                                        handleChange={handleRefraction}
                                        name="farVisionAxisLeft"
                                    />

                                    <SelectInput
                                        errorMessage={
                                            props.errors
                                                ?.farVisionVisualAcuityLeft
                                        }
                                        className="shadow-sm w-fit mt-1 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                        placeholder={true}
                                        value={
                                            refraction?.farVisionVisualAcuityLeft
                                        }
                                        options={visionValues.map((element) => (
                                            <option
                                                key={element.id}
                                                value={element.id}
                                                label={element.name}
                                            >
                                                {element.name}
                                            </option>
                                        ))}
                                        name="farVisionVisualAcuityLeft"
                                        handleChange={handleRefraction}
                                        labelClassName="w-full text-center block dark:text-gray-200"
                                    />
                                </div>
                            </div>
                            <div>
                                <h1 className="text-lg leading-6 font-medium text-black dark:text-white">
                                    Visión cercana
                                </h1>
                                <div className="grid grid-cols-6 gap-4 w-10/12 sm:w-full mt-4">
                                    <p className="text-end mt-7 dark:text-white">
                                        OD
                                    </p>
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.nearVisionSphericalRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.nearVisionSphericalRight
                                        }
                                        handleChange={handleRefraction}
                                        name="nearVisionSphericalRight"
                                        label="Esf"
                                        labelClassName="w-full text-center block"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.nearVisionCylindricalRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.nearVisionCylindricalRight
                                        }
                                        handleChange={handleRefraction}
                                        name="nearVisionCylindricalRight"
                                        label="Cil."
                                        labelClassName="w-full text-center block"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.nearVisionAxisRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={refraction?.nearVisionAxisRight}
                                        handleChange={handleRefraction}
                                        name="nearVisionAxisRight"
                                        label="Eje"
                                        labelClassName="w-full text-center block"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.additionRight
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={refraction?.additionRight}
                                        handleChange={handleRefraction}
                                        name="additionRight"
                                        label="Add"
                                        labelClassName="w-full text-center block"
                                    />
                                    <SelectInput
                                        errorMessage={
                                            props.errors
                                                ?.nearVisionVisualAcuityRight
                                        }
                                        containerStyle="-mt-1"
                                        className="shadow-sm w-fit mt-1 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                        placeholder={true}
                                        value={
                                            refraction?.nearVisionVisualAcuityRight
                                        }
                                        options={visionValues.map((element) => (
                                            <option
                                                key={element.id}
                                                value={element.id}
                                                label={element.name}
                                            >
                                                {element.name}
                                            </option>
                                        ))}
                                        name="nearVisionVisualAcuityRight"
                                        handleChange={handleRefraction}
                                        label="Av."
                                        labelClassName="w-full text-center block dark:text-gray-200"
                                    />
                                    <p className="text-end mt-2 dark:text-white">
                                        OI
                                    </p>
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.nearVisionSphericalLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.nearVisionSphericalLeft
                                        }
                                        handleChange={handleRefraction}
                                        name="nearVisionSphericalLeft"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors
                                                ?.nearVisionCylindricalLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={
                                            refraction?.nearVisionCylindricalLeft
                                        }
                                        handleChange={handleRefraction}
                                        name="nearVisionCylindricalLeft"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.nearVisionAxisLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={refraction?.nearVisionAxisLeft}
                                        handleChange={handleRefraction}
                                        name="nearVisionAxisLeft"
                                    />
                                    <TextInput
                                        showErrorMessage={true}
                                        errorMessage={
                                            props.errors?.additionLeft
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                /[0-9\.\+-]/.test(event.key) ||
                                                event.key === "Backspace" ||
                                                event.key === "Tab"
                                            ) {
                                            } else {
                                                event.preventDefault();
                                            }
                                        }}
                                        value={refraction?.additionLeft}
                                        handleChange={handleRefraction}
                                        name="additionLeft"
                                    />
                                    <SelectInput
                                        errorMessage={
                                            props.errors
                                                ?.nearVisionVisualAcuityLeft
                                        }
                                        className="shadow-sm w-fit mt-1 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                        placeholder={true}
                                        value={
                                            refraction?.nearVisionVisualAcuityLeft
                                        }
                                        options={visionValues.map((element) => (
                                            <option
                                                key={element.id}
                                                value={element.id}
                                                label={element.name}
                                            >
                                                {element.name}
                                            </option>
                                        ))}
                                        name="nearVisionVisualAcuityLeft"
                                        handleChange={handleRefraction}
                                        labelClassName="w-full text-center block"
                                    />
                                </div>
                            </div>
                            <TextInput
                                showErrorMessage={true}
                                errorMessage={
                                    props.errors?.intrapupillaryDistance
                                }
                                containerClassName="w-fit"
                                className="shadow-sm pl-2 w-20 border border-gray-300 block sm:text-sm rounded-md input-base"
                                onKeyDown={(event) => {
                                    if (
                                        /[0-9\.\+-]/.test(event.key) ||
                                        event.key === "Backspace" ||
                                        event.key === "Tab"
                                    ) {
                                    } else {
                                        event.preventDefault();
                                    }
                                }}
                                value={refraction?.intrapupillaryDistance}
                                handleChange={handleRefraction}
                                name="intrapupillaryDistance"
                                label="Distancia interpupilar"
                                labelClassName="text-center block dark:text-gray-200"
                            />
                            <TextEditor
                                error={props.errors?.generalObservations}
                                value={refraction.generalObservations}
                                handleChange={handleRefraction}
                                name="generalObservations"
                                label="Observaciones generales"
                            />
                        </div>
                    </TabSection>
                    <TabSection title="Receta de anteojos">
                        <div className="flex flex-col">
                            <div className="flex gap-3 p-4">
                                <Toggle
                                    value={generatePrescription}
                                    handleChange={setGeneratePrescription}
                                />
                                <p className="text-primary font-bold text-lg -mt-1">
                                    Generar receta de anteojos
                                </p>
                            </div>
                            {generatePrescription && (
                                <div className="flex flex-col gap-6 ml-6">
                                    <div>
                                        <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                            Visión lejana
                                        </h1>
                                        <div className="grid grid-cols-4 gap-4 w-10/12 sm:w-full mt-4 -ml-10">
                                            <p className="text-end mt-7 dark:text-white">
                                                OD
                                            </p>
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.farVisionSphericalRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="farVisionSphericalRight"
                                                label="Esf"
                                                labelClassName="w-full text-center block"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.farVisionCylindricalRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="farVisionCylindricalRight"
                                                label="Cil."
                                                labelClassName="w-full text-center block"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.farVisionAxisRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="farVisionAxisRight"
                                                label="Eje"
                                                labelClassName="w-full text-center block"
                                            />
                                            <p className="text-end mt-2 dark:text-white">
                                                OI
                                            </p>
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.farVisionSphericalLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="farVisionSphericalLeft"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.farVisionCylindricalLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="farVisionCylindricalLeft"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.farVisionAxisLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="farVisionAxisLeft"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                            Visión cercana
                                        </h1>
                                        <div className="grid grid-cols-5 gap-4 w-10/12 sm:w-full mt-4 -ml-10">
                                            <p className="text-end mt-7 dark:text-white">
                                                OD
                                            </p>
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.nearVisionSphericalRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="nearVisionSphericalRight"
                                                label="Esf"
                                                labelClassName="w-full text-center block"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.nearVisionCylindricalRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="nearVisionCylindricalRight"
                                                label="Cil."
                                                labelClassName="w-full text-center block"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.nearVisionAxisRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="nearVisionAxisRight"
                                                label="Eje"
                                                labelClassName="w-full text-center block"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.additionRight
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="additionRight"
                                                label="Add"
                                                labelClassName="w-full text-center block"
                                            />
                                            <p className="text-end mt-2 dark:text-white">
                                                OI
                                            </p>
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.nearVisionSphericalLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="nearVisionSphericalLeft"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.nearVisionCylindricalLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="nearVisionCylindricalLeft"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.nearVisionAxisLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="nearVisionAxisLeft"
                                            />
                                            <TextInput
                                                value={
                                                    refraction
                                                        ?.eyeglassPrescription
                                                        ?.additionLeft
                                                }
                                                handleChange={
                                                    handlePrescription
                                                }
                                                name="additionLeft"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 w-full sm:w-2/3 sm:ml-4">
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.photochromic
                                            }
                                            handleChange={handlePrescription}
                                            name="photochromic"
                                            label="Fotocromático"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.monofocal
                                            }
                                            handleChange={handlePrescription}
                                            name="monofocal"
                                            label="Monofocal"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.antiGlare
                                            }
                                            handleChange={handlePrescription}
                                            name="antiGlare"
                                            label="Antireflejo"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.bifocal
                                            }
                                            handleChange={handlePrescription}
                                            name="bifocal"
                                            label="Bifocal"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.uvx
                                            }
                                            handleChange={handlePrescription}
                                            name="uvx"
                                            label="UVX"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.multifocal
                                            }
                                            handleChange={handlePrescription}
                                            name="multifocal"
                                            label="Multifocal"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.focalFilter
                                            }
                                            handleChange={handlePrescription}
                                            name="focalFilter"
                                            label="Filtro azul"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.wideField
                                            }
                                            handleChange={handlePrescription}
                                            name="wideField"
                                            label="Campo Amplio"
                                        />
                                        <InlineCheckbox
                                            value={
                                                refraction?.eyeglassPrescription
                                                    ?.contactLens
                                            }
                                            handleChange={handlePrescription}
                                            name="contactLens"
                                            label="Lente de contacto"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </TabSection>
                </div>
            </div>
            <TabFooter
                leftAction={
                    <button
                        onClick={() => props.setTab("Visión y Tensión")}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:text-primary dark:bg-gray-900"
                    >
                        Anterior
                    </button>
                }
                rightAction={
                    <>
                        {refractionRecords && (
                            <button
                                type="button"
                                onClick={() => setRefractionRecordsOpen(true)}
                                className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                Ver consultas anteriores
                            </button>
                        )}
                        {downloadError && (
                            <p className="max-w-xs text-sm font-medium text-[#F51515]">
                                {downloadError}
                            </p>
                        )}
                        <div className="flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-black shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                            <Toggle
                                value={props.printWithInsurance}
                                handleChange={props.setPrintWithInsurance}
                            />
                            <p className="text-sm font-medium">
                                Imprimir con seguro
                            </p>
                        </div>
                        {loadingDownload ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary" />
                        ) : (
                            <button
                                onClick={() => downloadPDF()}
                                type="button"
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                <PdfIcon />
                                <span>Imprimir receta</span>
                            </button>
                        )}
                        <button
                            onClick={() => props.setTab("Medicación")}
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                        >
                            Siguiente
                        </button>
                    </>
                }
            />
        </div>
    );
};

const Medications = function (props) {
    const presentation = [
        { id: 1, name: "Colirio" },
        { id: 2, name: "Comprimidos" },
        { id: 3, name: "Ungüento" },
        { id: 4, name: "Gel" },
        { id: 5, name: "Loción" },
        { id: 6, name: "Pomada" },
    ];

    const frequency = [
        { id: 1, name: "Minutos" },
        { id: 2, name: "Horas" },
        { id: 3, name: "Días" },
        { id: 4, name: "Semanas" },
        { id: 5, name: "Meses" },
        { id: 6, name: "Libre demanda" },
    ];

    const period = [
        { id: 1, name: "Días" },
        { id: 2, name: "Semanas" },
        { id: 3, name: "Meses" },
        { id: 4, name: "Años" },
        { id: 5, name: "De por vida" },
    ];

    const eye = [
        { id: 1, name: "Derecho" },
        { id: 2, name: "Izquierdo" },
        { id: 3, name: "Ambos" },
    ];

    const [firstRender, setFirstRender] = useState(false);
    const [medications, setMedications] = useState([
        {
            medicine: "",
            quantity: "",
            presentation: 1,
            frequencyNumber: "",
            frequency: 2,
            periodNumber: "",
            period: 1,
            observations: "",
            delivered: false,
            eye: 3,
        },
    ]);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadError, setDownloadError] = useState(undefined);
    const [medicationsRecordsOpen, setMedicationsRecordsOpen] = useState(false);
    const [aux, setAux] = useState(undefined);
    const [selectedDate, setSelectedDate] = useState(undefined);
    const [nonMedicationTreatment, setNonMedicationTreatment] =
        useState(undefined);

    const handleMedication = (field, value, index) => {
        let obj = medications.slice();
        if (!obj[index]) {
            obj[index] = {
                medicine: "",
                quantity: "",
                presentation: 1,
                frequencyNumber: "",
                frequency: 2,
                periodNumber: "",
                period: 1,
                observations: "",
                delivered: false,
                eye: 3,
            };
        }
        if (field === "period" && value === 5) {
            delete obj[index].periodNumber;
        }

        if (field === "frequency" && value === 6) {
            delete obj[index].frequencyNumber;
        }

        if (field === "presentation" && value !== 1 && value !== 6) {
            obj[index].eye = null;
        }

        obj[index][field] = value;
        setMedications(obj);
    };
    const addMedication = () =>
        setMedications((prev) => [
            ...prev,
            {
                medicine: "",
                quantity: "",
                presentation: 1,
                frequencyNumber: "",
                frequency: 2,
                periodNumber: "",
                period: 1,
                observations: "",
                delivered: false,
                eye: 3,
            },
        ]);
    const removeMedication = (indx) => {
        let auxMeds = medications.slice(0);
        auxMeds.splice(indx, 1);
        setMedications(auxMeds);
    };
    const handleAux = (field, value) => setAux(value);

    const createMedication = async (name) => {
        let response = await api.medicines.create(name);
        return response;
    };

    const handleNonMedicationTreatment = (field, value) =>
        setNonMedicationTreatment(value);

    useEffect(() => {
        if (medications[0]?.medicine !== "")
            props.handleChange(
                "medications",
                medications.map((element) => ({
                    ...element,
                    medicine: element.medicine,
                })),
            );
    }, [medications]);

    useEffect(() => {
        if (nonMedicationTreatment || nonMedicationTreatment === "")
            props.handleChange(
                "nonMedicationTreatment",
                nonMedicationTreatment,
            );
    }, [nonMedicationTreatment]);

    useEffect(() => {
        if (props.consultation.medications && !firstRender) {
            setMedications(props.consultation.medications);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, firstRender]);

    useEffect(() => {
        if (props.consultation.nonMedicationTreatment && !firstRender) {
            setNonMedicationTreatment(
                props.consultation.nonMedicationTreatment,
            );
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, firstRender]);

    const checkIfEmpty = (medication) => {
        if (
            medication.medicine ||
            medication.periodNumber !== "" ||
            medication.quantity !== "" ||
            medication.frequencyNumber !== "" ||
            medication.observations !== ""
        )
            return false;
        else return true;
    };

    const downloadPDF = function () {
        setLoadingDownload(true);
        setDownloadError("");
        let request = {};
        if (
            props.consultation.medications &&
            !props.consultation.medications
                .map((element) => (checkIfEmpty(element) ? null : element))
                .every((element) => element === null)
        ) {
            request = {
                ...props.consultation,
                medications: props.consultation.medications.map((element) =>
                    element.period === 5
                        ? { ...element, periodNumber: 0 }
                        : element,
                ),
            };
        } else {
            request = { ...props.consultation };
            delete request.medications;
        }

        request = { ...request, printWithInsurance: props.printWithInsurance };
        api.medication.get(request).then(
            (res) => {
                setLoadingDownload(false);
                const url = window.URL.createObjectURL(new Blob([res]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "Medicamentos.pdf");
                document.body.appendChild(link);
                link.click();
            },
            async (err) => {
                setDownloadError(
                    JSON.parse(await err.response.data.text()).detail,
                );
                setLoadingDownload(false);
            },
        );
    };

    useEffect(() => {
        if (!medicationsRecordsOpen) {
            setSelectedDate(undefined);
            setAux(undefined);
        }
    }, [medicationsRecordsOpen]);

    const medicationsRecords = (
        <>
            {props.medicationsRecords && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div
                        className={classNames(
                            selectedDate ? "lg:col-span-1" : "lg:col-span-3",
                            "mt-3 text-center sm:mt-5",
                        )}
                    >
                        <Dialog.Title
                            as="h3"
                            className="leading-6 text-[#75C9EE] font-semibold text-2xl"
                        >
                            Consultas anteriores
                        </Dialog.Title>
                        <div className="mt-8">
                            <SelectInput
                                containerStyle="mx-auto"
                                placeholder={true}
                                placeholderText="Seleccionar fecha"
                                value={aux}
                                options={props.medicationsRecords.map(
                                    (element, idx) => (
                                        <option
                                            key={idx}
                                            value={idx + 1}
                                            label={formatDateDDMMYYYY(element.consultationDate)}
                                        >
                                            {formatDateDDMMYYYY(element.consultationDate)}
                                        </option>
                                    ),
                                )}
                                name="aux"
                                handleChange={handleAux}
                                label="Selecciona una fecha"
                            />
                            <button
                                type="button"
                                onClick={() => setSelectedDate(aux)}
                                className="w-full mt-10 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                            >
                                Buscar
                            </button>
                            <button
                                type="button"
                                onClick={() => setMedicationsRecordsOpen(false)}
                                className="w-full mt-4 inline-flex justify-center rounded-md border shadow-sm px-4 py-2 bg-white text-base font-medium text-black sm:col-start-2 sm:text-sm"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                    {selectedDate && (
                        <div className="flex flex-col gap-3 lg:col-span-2">
                            <p className="text-primary font-semibold text-sm leading-[20px]">
                                {
                                    formatDateDDMMYYYY(
                                        props.medicationsRecords[selectedDate - 1]
                                            .consultationDate
                                    )
                                }
                            </p>
                            <div className="hidden grid-cols-8 gap-6 text-center text-sm font-semibold leading-[16px] lg:grid">
                                <p className="col-span-2">Medicamento</p>
                                <p className="col-span-1">Cant.</p>
                                <p className="col-span-2">Presentación</p>
                                <p className="col-span-1">Freq.</p>
                                <p className="col-span-2">Periodo</p>
                            </div>
                            <div className="space-y-3">
                                {props.medicationsRecords[
                                    selectedDate - 1
                                ].medications.map((element) => (
                                    <div
                                        key={element.id}
                                        className="rounded-[12px] border border-[#75C9EE] bg-[#FAFAFA] px-4 py-3 lg:grid lg:grid-cols-8 lg:gap-6 lg:rounded-[7px] lg:py-2 lg:text-center"
                                    >
                                        <div className="grid grid-cols-1 gap-2 text-sm lg:col-span-8 lg:hidden">
                                            <p><span className="font-semibold">Medicamento:</span> {element.medicine.name}</p>
                                            <p><span className="font-semibold">Cantidad:</span> {element.quantity}</p>
                                            <p><span className="font-semibold">Presentación:</span> {presentation[element.presentation - 1]?.name}</p>
                                            <p><span className="font-semibold">Frecuencia:</span> {element.frequency === 6 ? "A libre demanda" : `${element.frequencyNumber} ${frequency[element.frequency - 1].name}`}</p>
                                            <p><span className="font-semibold">Periodo:</span> {`${element.periodNumber} ${period[element.period - 1].name}`}</p>
                                        </div>
                                        <p className="hidden lg:col-span-2 lg:block">
                                            {element.medicine.name}
                                        </p>
                                        <p className="hidden lg:col-span-1 lg:block">
                                            {element.quantity}
                                        </p>
                                        <p className="hidden lg:col-span-2 lg:block">
                                            {presentation[element.presentation - 1]?.name}
                                        </p>
                                        <p className="hidden lg:col-span-1 lg:block">{element.frequency === 6 ? "A libre demanda" : `${element.frequencyNumber} ${frequency[element.frequency - 1].name}`}</p>
                                        <p className="hidden lg:col-span-2 lg:block">{`${element.periodNumber} ${period[element.period - 1].name}`}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div>
            <Modal
                size={selectedDate ? "sm:max-w-5xl w-full" : "sm:max-w-5xl "}
                content={medicationsRecords}
                open={medicationsRecordsOpen}
                setOpen={setMedicationsRecordsOpen}
            />
            <div className="space-y-5 px-4 py-5 sm:p-4">
                <TabSection
                    title="Indicaciones"
                >
                    <div className="mt-2 flex-col flex-1 space-y-6">
                        <TextEditor
                            value={nonMedicationTreatment}
                            handleChange={handleNonMedicationTreatment}
                            name="nonMedicationTreatment"
                            label="Tratamiento no medicamentoso"
                        />
                        <>
                            <div className="flex flex-col gap-4 sm:flex-row sm:gap-x-4">
                                <SimpleCombobox
                                    error={
                                        props.errors &&
                                        props.errors[0]?.medicine
                                            ? props.errors[0]?.medicine
                                            : null
                                    }
                                    create={createMedication}
                                    required={true}
                                    value={medications[0]?.medicine}
                                    containerClassName="w-full"
                                    Idx={0}
                                    handleChange={handleMedication}
                                    name="medicine"
                                    label={`Medicamento 1`}
                                    options={props.medicines}
                                />
                                <TextInput
                                    onKeyDown={(event) => {
                                        if (
                                            /[0-9]/.test(event.key) ||
                                            event.key === "Backspace" ||
                                            event.key === "Tab"
                                        ) {
                                        } else {
                                            event.preventDefault();
                                        }
                                    }}
                                    showErrorMessage={true}
                                    errorMessage={
                                        props.errors &&
                                        props.errors[0]?.quantity
                                            ? props.errors[0]?.quantity
                                            : null
                                    }
                                    parse={true}
                                    required={true}
                                    value={medications[0]?.quantity}
                                    containerClassName="max-w-[80px]"
                                    className="mt-1 shadow-sm pl-2 w-full border border-gray-300 block sm:text-sm rounded-md input-base"
                                    name="quantity"
                                    Idx={0}
                                    handleChange={handleMedication}
                                    label="Cantidad"
                                    labelClassName="whitespace-nowrap"
                                />
                                <SelectInput
                                    required={true}
                                    value={medications[0]?.presentation}
                                    Idx={0}
                                    name="presentation"
                                    handleChange={handleMedication}
                                    label="Presentación"
                                    containerStyle="w-fit"
                                    className="shadow-sm w-fit mt-2 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                    options={presentation.map((element) => (
                                        <option
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                />
                                <div className="flex flex-col w-1/2">
                                    <div className="flex ">
                                        <TextInput
                                            onKeyDown={(event) => {
                                                if (
                                                    /[0-9]/.test(event.key) ||
                                                    event.key === "Backspace" ||
                                                    event.key === "Tab"
                                                ) {
                                                } else {
                                                    event.preventDefault();
                                                }
                                            }}
                                            errorMessage={
                                                props.errors &&
                                                props.errors[0]?.frequencyNumber
                                                    ? props.errors[0]
                                                          ?.frequencyNumber
                                                    : null
                                            }
                                            disabled={
                                                medications[0]?.frequency === 6
                                            }
                                            parse={true}
                                            value={
                                                medications[0]?.frequency === 6
                                                    ? ""
                                                    : medications[0]
                                                          ?.frequencyNumber
                                            }
                                            label="Label"
                                            labelClassName="invisible"
                                            className={classNames(
                                                medications[0]?.frequency === 6
                                                    ? "bg-gray-200"
                                                    : "bg-none",
                                                "shadow-sm pl-2 w-12 mt-1 border rounded-r-none border-gray-300 block sm:text-sm rounded-md input-base",
                                            )}
                                            Idx={0}
                                            name="frequencyNumber"
                                            handleChange={handleMedication}
                                        />
                                        <SelectInput
                                            required={true}
                                            value={medications[0]?.frequency}
                                            Idx={0}
                                            name="frequency"
                                            handleChange={handleMedication}
                                            label="Frecuencia"
                                            containerStyle="w-full"
                                            className="shadow-sm w-full mt-2 mx-auto pr-10 border rounded-l-none border-gray-300 block sm:text-sm rounded-md input-base"
                                            options={frequency.map(
                                                (element) => (
                                                    <option
                                                        value={element.id}
                                                        label={element.name}
                                                    >
                                                        {element.name}
                                                    </option>
                                                ),
                                            )}
                                        />
                                    </div>
                                    {props.errors &&
                                        props.errors[0]?.frequencyNumber && (
                                            <p className="mt-2 text-sm text-red-600">
                                                {
                                                    props.errors[0]
                                                        ?.frequencyNumber
                                                }
                                            </p>
                                        )}
                                </div>
                                <div className="flex flex-col w-1/2">
                                    <div className="flex">
                                        <TextInput
                                            onKeyDown={(event) => {
                                                if (
                                                    /[0-9]/.test(event.key) ||
                                                    event.key === "Backspace" ||
                                                    event.key === "Tab"
                                                ) {
                                                } else {
                                                    event.preventDefault();
                                                }
                                            }}
                                            errorMessage={
                                                props.errors &&
                                                props.errors[0]?.periodNumber
                                                    ? props.errors[0]
                                                          ?.periodNumber
                                                    : null
                                            }
                                            disabled={
                                                medications[0]?.period === 5
                                            }
                                            parse={true}
                                            value={
                                                medications[0]?.period === 5
                                                    ? ""
                                                    : medications[0]
                                                          ?.periodNumber
                                            }
                                            label="Label"
                                            labelClassName="invisible"
                                            className={classNames(
                                                medications[0]?.period === 5
                                                    ? "bg-gray-200"
                                                    : "bg-none",
                                                "shadow-sm pl-2 w-12 mt-1 border rounded-r-none border-gray-300 block sm:text-sm rounded-md input-base",
                                            )}
                                            Idx={0}
                                            name="periodNumber"
                                            handleChange={handleMedication}
                                        />
                                        <SelectInput
                                            required={false}
                                            value={medications[0]?.period}
                                            Idx={0}
                                            name="period"
                                            handleChange={handleMedication}
                                            label="Periodo"
                                            containerStyle="w-full"
                                            className="shadow-sm w-full mt-2 mx-auto pr-10 border rounded-l-none border-gray-300 block sm:text-sm rounded-md input-base"
                                            options={period.map((element) => (
                                                <option
                                                    value={element.id}
                                                    label={element.name}
                                                >
                                                    {element.name}
                                                </option>
                                            ))}
                                        />
                                    </div>
                                    {props.errors &&
                                        props.errors[0]?.periodNumber && (
                                            <p className="mt-2 text-sm text-red-600">
                                                {props.errors[0]?.periodNumber}
                                            </p>
                                        )}
                                </div>
                                <SelectInput
                                    required={false}
                                    disabled={
                                        medications[0]?.presentation !== 1 &&
                                        medications[0]?.presentation !== 6
                                    }
                                    placeholder={true}
                                    placeholderText="-- Seleccionar --"
                                    value={medications[0]?.eye || ""}
                                    Idx={0}
                                    name="eye"
                                    handleChange={handleMedication}
                                    label="Ojo"
                                    containerStyle="w-fit"
                                    errorMessage={
                                        props.errors && props.errors[0]?.eye
                                            ? props.errors[0]?.eye
                                            : null
                                    }
                                    className="shadow-sm w-24 mt-2 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                    options={eye.map((element) => (
                                        <option
                                            value={element.id}
                                            label={element.name}
                                        >
                                            {element.name}
                                        </option>
                                    ))}
                                />
                                <Checkbox
                                    value={medications[0]?.delivered}
                                    handleChange={handleMedication}
                                    Idx={0}
                                    name="delivered"
                                    label={
                                        <>
                                            <p>Entregado al paciente</p>
                                        </>
                                    }
                                />
                            </div>
                            <TextEditor
                                value={medications[0]?.observations}
                                handleChange={(field, value) =>
                                    handleMedication(field, value, 0)
                                }
                                name="observations"
                                label={`Observaciones del medicamento ${1}`}
                            />
                        </>
                        {medications.length > 0 ? (
                            <>
                                {medications.map((element, Idx) => {
                                    if (Idx !== 0)
                                        return (
                                            <React.Fragment
                                                key={element.id ?? Idx}
                                            >
                                                <div className="flex flex-col gap-4 sm:flex-row sm:gap-x-4">
                                                    <SimpleCombobox
                                                        error={
                                                            props.errors &&
                                                            props.errors[Idx]
                                                                ?.medicine
                                                                ? props.errors[
                                                                      Idx
                                                                  ]?.medicine
                                                                : null
                                                        }
                                                        create={
                                                            createMedication
                                                        }
                                                        required={true}
                                                        value={element.medicine}
                                                        Idx={Idx}
                                                        handleChange={
                                                            handleMedication
                                                        }
                                                        name="medicine"
                                                        label={`Medicamento ${
                                                            Idx + 1
                                                        }`}
                                                        containerClassName="w-full"
                                                        options={
                                                            props.medicines
                                                        }
                                                    />
                                                    <TextInput
                                                        onKeyDown={(event) => {
                                                            if (
                                                                /[0-9]/.test(
                                                                    event.key,
                                                                ) ||
                                                                event.key ===
                                                                    "Backspace" ||
                                                                event.key ===
                                                                    "Tab"
                                                            ) {
                                                            } else {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                        showErrorMessage={true}
                                                        errorMessage={
                                                            props.errors &&
                                                            props.errors[Idx]
                                                                ?.quantity
                                                                ? props.errors[
                                                                      Idx
                                                                  ]?.quantity
                                                                : null
                                                        }
                                                        parse={true}
                                                        required={true}
                                                        containerClassName="max-w-[80px]"
                                                        className="mt-1 shadow-sm pl-2 w-full border border-gray-300 block sm:text-sm rounded-md input-base"
                                                        value={element.quantity}
                                                        name="quantity"
                                                        Idx={Idx}
                                                        handleChange={
                                                            handleMedication
                                                        }
                                                        label="Cantidad"
                                                        labelClassName="whitespace-nowrap"
                                                    />
                                                    <SelectInput
                                                        required={true}
                                                        value={
                                                            element.presentation
                                                        }
                                                        Idx={Idx}
                                                        name="presentation"
                                                        handleChange={
                                                            handleMedication
                                                        }
                                                        label="Presentación"
                                                        containerStyle="w-fit"
                                                        className="shadow-sm w-fit mt-2 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                                        options={presentation.map(
                                                            (element) => (
                                                                <option
                                                                    value={
                                                                        element.id
                                                                    }
                                                                    label={
                                                                        element.name
                                                                    }
                                                                >
                                                                    {
                                                                        element.name
                                                                    }
                                                                </option>
                                                            ),
                                                        )}
                                                    />
                                                    <div className="flex-col w-1/2">
                                                        <div className="flex items-end">
                                                            <TextInput
                                                                onKeyDown={(
                                                                    event,
                                                                ) => {
                                                                    if (
                                                                        /[0-9]/.test(
                                                                            event.key,
                                                                        ) ||
                                                                        event.key ===
                                                                            "Backspace" ||
                                                                        event.key ===
                                                                            "Tab"
                                                                    ) {
                                                                    } else {
                                                                        event.preventDefault();
                                                                    }
                                                                }}
                                                                disabled={
                                                                    element?.frequency ===
                                                                    6
                                                                        ? true
                                                                        : false
                                                                }
                                                                parse={true}
                                                                className={classNames(
                                                                    element?.frequency ===
                                                                        6
                                                                        ? "bg-gray-200"
                                                                        : "bg-none",
                                                                    "shadow-sm pl-2 w-12 border rounded-r-none border-gray-300 block sm:text-sm rounded-md input-base",
                                                                )}
                                                                value={
                                                                    element?.frequency ===
                                                                    6
                                                                        ? ""
                                                                        : element.frequencyNumber
                                                                }
                                                                Idx={Idx}
                                                                name="frequencyNumber"
                                                                handleChange={
                                                                    handleMedication
                                                                }
                                                            />
                                                            <SelectInput
                                                                required={true}
                                                                value={
                                                                    element.frequency
                                                                }
                                                                Idx={Idx}
                                                                name="frequency"
                                                                handleChange={
                                                                    handleMedication
                                                                }
                                                                label="Frecuencia"
                                                                containerStyle="w-full"
                                                                className="shadow-sm w-full mt-1 mx-auto pr-10 border rounded-l-none border-gray-300 block sm:text-sm rounded-md input-base"
                                                                options={frequency.map(
                                                                    (
                                                                        element,
                                                                    ) => (
                                                                        <option
                                                                            value={
                                                                                element.id
                                                                            }
                                                                            label={
                                                                                element.name
                                                                            }
                                                                        >
                                                                            {
                                                                                element.name
                                                                            }
                                                                        </option>
                                                                    ),
                                                                )}
                                                            />
                                                        </div>
                                                        {props.errors &&
                                                            props.errors[Idx]
                                                                ?.frequencyNumber && (
                                                                <p className="mt-2 text-sm text-red-600">
                                                                    {
                                                                        props
                                                                            .errors[
                                                                            Idx
                                                                        ]
                                                                            ?.frequencyNumber
                                                                    }
                                                                </p>
                                                            )}
                                                    </div>
                                                    <div className="flex-col w-1/2">
                                                        <div className="flex items-end">
                                                            <TextInput
                                                                onKeyDown={(
                                                                    event,
                                                                ) => {
                                                                    if (
                                                                        /[0-9]/.test(
                                                                            event.key,
                                                                        ) ||
                                                                        event.key ===
                                                                            "Backspace" ||
                                                                        event.key ===
                                                                            "Tab"
                                                                    ) {
                                                                    } else {
                                                                        event.preventDefault();
                                                                    }
                                                                }}
                                                                disabled={
                                                                    element?.period ===
                                                                    5
                                                                        ? true
                                                                        : false
                                                                }
                                                                parse={true}
                                                                className={classNames(
                                                                    element?.period ===
                                                                        5
                                                                        ? "bg-gray-200"
                                                                        : "bg-none",
                                                                    "shadow-sm pl-2 w-12 border rounded-r-none border-gray-300 block sm:text-sm rounded-md input-base",
                                                                )}
                                                                value={
                                                                    element?.period ===
                                                                    5
                                                                        ? ""
                                                                        : element.periodNumber
                                                                }
                                                                Idx={Idx}
                                                                name="periodNumber"
                                                                handleChange={
                                                                    handleMedication
                                                                }
                                                            />
                                                            <SelectInput
                                                                required={false}
                                                                value={
                                                                    element.period
                                                                }
                                                                Idx={Idx}
                                                                name="period"
                                                                handleChange={
                                                                    handleMedication
                                                                }
                                                                label="Periodo"
                                                                containerStyle="w-full"
                                                                className="shadow-sm w-full mt-1 mx-auto pr-10 border rounded-l-none border-gray-300 block sm:text-sm rounded-md input-base"
                                                                options={period.map(
                                                                    (
                                                                        element,
                                                                    ) => (
                                                                        <option
                                                                            value={
                                                                                element.id
                                                                            }
                                                                            label={
                                                                                element.name
                                                                            }
                                                                        >
                                                                            {
                                                                                element.name
                                                                            }
                                                                        </option>
                                                                    ),
                                                                )}
                                                            />
                                                        </div>
                                                        {props.errors &&
                                                            props.errors[Idx]
                                                                ?.periodNumber && (
                                                                <p className="mt-2 text-sm text-red-600">
                                                                    {
                                                                        props
                                                                            .errors[
                                                                            Idx
                                                                        ]
                                                                            ?.periodNumber
                                                                    }
                                                                </p>
                                                            )}
                                                    </div>
                                                    <SelectInput
                                                        required={false}
                                                        disabled={
                                                            element.presentation !==
                                                            1
                                                        }
                                                        placeholder={true}
                                                        placeholderText="-- Seleccionar --"
                                                        value={
                                                            element.eye || ""
                                                        }
                                                        Idx={Idx}
                                                        name="eye"
                                                        handleChange={
                                                            handleMedication
                                                        }
                                                        label="Ojo"
                                                        containerStyle="w-fit"
                                                        errorMessage={
                                                            props.errors &&
                                                            props.errors[Idx]
                                                                ?.eye
                                                                ? props.errors[
                                                                      Idx
                                                                  ]?.eye
                                                                : null
                                                        }
                                                        className="shadow-sm w-24 mt-2 mx-auto pr-6 border border-gray-300 block sm:text-sm rounded-md input-base"
                                                        options={eye.map(
                                                            (e) => (
                                                                <option
                                                                    value={e.id}
                                                                    label={
                                                                        e.name
                                                                    }
                                                                >
                                                                    {e.name}
                                                                </option>
                                                            ),
                                                        )}
                                                    />
                                                    <Checkbox
                                                        value={
                                                            element.delivered
                                                        }
                                                        handleChange={
                                                            handleMedication
                                                        }
                                                        Idx={Idx}
                                                        name="delivered"
                                                        label={
                                                            <>
                                                                <p>
                                                                    Entregado al
                                                                    paciente
                                                                </p>
                                                            </>
                                                        }
                                                    />
                                                </div>
                                                <div className="flex mt-2 gap-x-4">
                                                    <TextEditor
                                                        containerClassName="w-full"
                                                        value={
                                                            medications[Idx]
                                                                .observations
                                                        }
                                                        handleChange={(
                                                            field,
                                                            value,
                                                        ) =>
                                                            handleMedication(
                                                                field,
                                                                value,
                                                                Idx,
                                                            )
                                                        }
                                                        name="observations"
                                                        label={`Observaciones del medicamento ${
                                                            Idx + 1
                                                        }`}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="text-red-600 hover:text-red-900 mt-8 ml-3 pb-1.5"
                                                        onClick={() =>
                                                            removeMedication(
                                                                Idx,
                                                            )
                                                        }
                                                    >
                                                        <TrashIcon
                                                            className="h-6 w-6"
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </div>
                                            </React.Fragment>
                                        );
                                })}
                            </>
                        ) : null}
                    </div>
                    <div className="w-full flex justify-center">
                        <button
                            onClick={() => addMedication()}
                            type="button"
                            className="mt-8 inline-flex items-center px-4 py-2 border border-transparent dark:border-gray-700 shadow-sm text-sm font-semibold rounded-md text-[#75C9EE] bg-[#F8F8F8] dark:bg-gray-900 hover:bg-[#F8F8F8]"
                        >
                            <PlusIcon
                                className="-ml-1 mr-2 h-5 w-5"
                                aria-hidden="true"
                            />
                            Agregar otro medicamento
                        </button>
                    </div>
                </TabSection>
            </div>
            <TabFooter
                leftAction={
                    <button
                        onClick={() => props.setTab("Refracción")}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:text-primary dark:bg-gray-900"
                    >
                        Anterior
                    </button>
                }
                rightAction={
                    <>
                        {props.medicationsRecords && (
                            <button
                                type="button"
                                onClick={() => setMedicationsRecordsOpen(true)}
                                className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                Ver consultas anteriores
                            </button>
                        )}
                        {downloadError && (
                            <p className="max-w-xs text-sm font-medium text-[#F51515]">
                                {downloadError}
                            </p>
                        )}
                        <div className="flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-black shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                            <Toggle
                                value={props.printWithInsurance}
                                handleChange={props.setPrintWithInsurance}
                            />
                            <p className="text-sm font-medium">
                                Imprimir con seguro
                            </p>
                        </div>
                        {loadingDownload ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary" />
                        ) : (
                            <button
                                onClick={() => downloadPDF()}
                                type="button"
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                <PdfIcon />
                                <span>Imprimir receta</span>
                            </button>
                        )}
                        <button
                            onClick={() =>
                                props.setTab("Estudios oftalmológicos")
                            }
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                        >
                            Siguiente
                        </button>
                    </>
                }
            />
        </div>
    );
};

const OphthalmologicalStudies = function (props) {
    const [ophthalmologicalStudy, setOphthalmologicalStudy] = useState({});
    const [firstRender, setFirstRender] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadError, setDownloadError] = useState(undefined);
    const [selectAll, setSelectAll] = useState(false);
    const [procedures, setProcedures] = useState([]);
    const [procedureCreateError, setProcedureCreateError] = useState("");

    const handleOphthalmologicalStudy = (field, value) =>
        setOphthalmologicalStudy((prev) => ({ ...prev, [field]: value }));

    useEffect(() => {
        api.surgeries.getMulti().then((response) => {
            setProcedures(response);
        });
    }, []);

    const createProcedure = async (name) => {
        try {
            setProcedureCreateError("");
            let response = await api.surgeries.create(name);
            setProcedures((prev) => [...prev, response]);
            return response;
        } catch (e) {
            const msg =
                e?.message?.name?.[0] ||
                e?.message?.detail ||
                "No se pudo crear el procedimiento";
            setProcedureCreateError(msg);
            return Promise.reject(e);
        }
    };

    useEffect(() => {
        if (selectAll) {
            handleOphthalmologicalStudy("ultrasound", true);
            handleOphthalmologicalStudy("tonalCurve", true);
            handleOphthalmologicalStudy("echobiometry", true);
            handleOphthalmologicalStudy("gonioscopy", true);
            handleOphthalmologicalStudy("specularMicroscopy", true);
            handleOphthalmologicalStudy("pachymetry", true);
            handleOphthalmologicalStudy("retinalStudy", true);
            handleOphthalmologicalStudy("topography", true);
            handleOphthalmologicalStudy("orthopticExam", true);
            handleOphthalmologicalStudy("visualField", true);
            handleOphthalmologicalStudy("lambdaTest", true);
            handleOphthalmologicalStudy("orthopticExercises", true);
            handleOphthalmologicalStudy("octRetina", true);
            handleOphthalmologicalStudy("octCornea", true);
            handleOphthalmologicalStudy("octGlaucoma", true);
            handleOphthalmologicalStudy("ubm", true);
        } else {
            handleOphthalmologicalStudy("ultrasound", false);
            handleOphthalmologicalStudy("tonalCurve", false);
            handleOphthalmologicalStudy("echobiometry", false);
            handleOphthalmologicalStudy("gonioscopy", false);
            handleOphthalmologicalStudy("specularMicroscopy", false);
            handleOphthalmologicalStudy("pachymetry", false);
            handleOphthalmologicalStudy("retinalStudy", false);
            handleOphthalmologicalStudy("topography", false);
            handleOphthalmologicalStudy("orthopticExam", false);
            handleOphthalmologicalStudy("visualField", false);
            handleOphthalmologicalStudy("lambdaTest", false);
            handleOphthalmologicalStudy("orthopticExercises", false);
            handleOphthalmologicalStudy("octRetina", false);
            handleOphthalmologicalStudy("octCornea", false);
            handleOphthalmologicalStudy("octGlaucoma", false);
            handleOphthalmologicalStudy("ubm", false);
        }
    }, [selectAll]);

    useEffect(() => {
        if (props.consultation.ophthalmologicalStudy && !firstRender) {
            setOphthalmologicalStudy(props.consultation.ophthalmologicalStudy);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, firstRender]);

    useEffect(() => {
        props.handleChange("ophthalmologicalStudy", ophthalmologicalStudy);
    }, [ophthalmologicalStudy]);

    const downloadPDF = function () {
        setLoadingDownload(true);
        setDownloadError("");
        api.ophthalmologicalStudy
            .get({
                patient: props.consultation.patient,
                doctor: props.consultation.doctor,
                generalObservations:
                    props.consultation.ophthalmologicalStudy
                        .generalObservations,
                ophthalmologicalStudy: props.consultation.ophthalmologicalStudy,
                printWithInsurance: props.printWithInsurance,
            })
            .then(
                (res) => {
                    setLoadingDownload(false);
                    const url = window.URL.createObjectURL(new Blob([res]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "EstudiosOftalmologicos.pdf");
                    document.body.appendChild(link);
                    link.click();
                },
                async (err) => {
                    setDownloadError(
                        JSON.parse(await err.response.data.text()).detail,
                    );
                    setLoadingDownload(false);
                },
            );
    };

    return (
        <div>
            <div className="space-y-5 px-4 py-6 sm:p-4">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                    <TabSection
                        title="Orden de estudio"
                    >
                        <div className="flex flex-col gap-6">
                        <div>
                            <span
                                onClick={() => setSelectAll(!selectAll)}
                                className="mt-2 text-primary underline cursor-pointer"
                            >
                                {selectAll
                                    ? "Deseleccionar todos"
                                    : "Seleccionar todos"}
                            </span>
                            <div className="mt-2 grid grid-cols-2 gap-y-4">
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.ultrasound}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="ultrasound"
                                    label="Ecografía"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.tonalCurve}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="tonalCurve"
                                    label="Curva tensional"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.echobiometry}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="echobiometry"
                                    label="Ecobiometría"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.gonioscopy}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="gonioscopy"
                                    label="Gonioscopia"
                                />
                                <InlineCheckbox
                                    value={
                                        ophthalmologicalStudy?.specularMicroscopy
                                    }
                                    handleChange={handleOphthalmologicalStudy}
                                    name="specularMicroscopy"
                                    label="Microscopia especular"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.pachymetry}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="pachymetry"
                                    label="Paquimetría"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.retinalStudy}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="retinalStudy"
                                    label="Mapeo de retina"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.topography}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="topography"
                                    label="Topografía"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.orthopticExam}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="orthopticExam"
                                    label="Examen ortópico"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.visualField}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="visualField"
                                    label="Campo visual"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.lambdaTest}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="lambdaTest"
                                    label="Test de lambda"
                                />
                                <InlineCheckbox
                                    value={
                                        ophthalmologicalStudy?.orthopticExercises
                                    }
                                    handleChange={handleOphthalmologicalStudy}
                                    name="orthopticExercises"
                                    label="Ejercicios ortópicos"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.octRetina}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="octRetina"
                                    label="OCT Retina"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.octCornea}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="octCornea"
                                    label="OCT Cornea"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.octGlaucoma}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="octGlaucoma"
                                    label="OCT Glaucoma"
                                />
                                <InlineCheckbox
                                    value={ophthalmologicalStudy?.ubm}
                                    handleChange={handleOphthalmologicalStudy}
                                    name="ubm"
                                    label="UBM"
                                />
                            </div>
                        </div>
                        </div>
                    </TabSection>
                    <TabSection
                        title="Complementos"
                    >
                        <div className="flex flex-col gap-6 p-1">
                        <CustomCombobox
                            create={createProcedure}
                            multiple={true}
                            value={ophthalmologicalStudy?.otherStudies}
                            error={
                                procedureCreateError ||
                                props.errors?.otherStudies
                            }
                            handleChange={handleOphthalmologicalStudy}
                            options={procedures}
                            name="otherStudies"
                            containerClassName="col-span-1 w-full relative"
                            label="Agregar otro estudio"
                        />
                        <TextEditor
                            error={props.errors?.generalObservations}
                            value={ophthalmologicalStudy?.generalObservations}
                            handleChange={handleOphthalmologicalStudy}
                            name="generalObservations"
                            label="Observaciones generales"
                        />
                        </div>
                    </TabSection>
                </div>
            </div>
            <TabFooter
                leftAction={
                    <button
                        onClick={() => props.setTab("Medicación")}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:text-primary dark:bg-gray-900"
                    >
                        Anterior
                    </button>
                }
                rightAction={
                    <>
                        {downloadError && (
                            <p className="max-w-xs text-sm font-medium text-[#F51515]">
                                {downloadError}
                            </p>
                        )}
                        <div className="flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-black shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                            <Toggle
                                value={props.printWithInsurance}
                                handleChange={props.setPrintWithInsurance}
                            />
                            <p className="text-sm font-medium">
                                Imprimir con seguro
                            </p>
                        </div>
                        {loadingDownload ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary" />
                        ) : (
                            <button
                                onClick={() => downloadPDF()}
                                type="button"
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                <PdfIcon />
                                <span>Imprimir receta</span>
                            </button>
                        )}
                        <button
                            onClick={() => props.setTab("Estudios clínicos")}
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                        >
                            Siguiente
                        </button>
                    </>
                }
            />
        </div>
    );
};

const ClinicalStudies = function (props) {
    const [clinicalStudy, setClinicalStudy] = useState({});
    const [firstRender, setFirstRender] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadError, setDownloadError] = useState(undefined);
    const [selectAll, setSelectAll] = useState(false);

    const handleClinicalStudy = (field, value) =>
        setClinicalStudy((prev) => ({ ...prev, [field]: value }));

    useEffect(() => {
        if (props.age) {
            handleClinicalStudy("age", props.age);
        }
    }, [props.age]);

    useEffect(() => {
        if (selectAll) {
            handleClinicalStudy("hemogram", true);
            handleClinicalStudy("vdrl", true);
            handleClinicalStudy("glycemia", true);
            handleClinicalStudy("bloodCrasis", true);
            handleClinicalStudy("urea", true);
            handleClinicalStudy("urineAnalysis", true);
            handleClinicalStudy("creatinine", true);
            handleClinicalStudy("stool", true);
            handleClinicalStudy("lipidProfile", true);
            handleClinicalStudy("worms", true);
            handleClinicalStudy("hiv", true);
            handleClinicalStudy("protozoa", true);
        } else {
            handleClinicalStudy("hemogram", false);
            handleClinicalStudy("vdrl", false);
            handleClinicalStudy("glycemia", false);
            handleClinicalStudy("bloodCrasis", false);
            handleClinicalStudy("urea", false);
            handleClinicalStudy("urineAnalysis", false);
            handleClinicalStudy("creatinine", false);
            handleClinicalStudy("stool", false);
            handleClinicalStudy("lipidProfile", false);
            handleClinicalStudy("worms", false);
            handleClinicalStudy("hiv", false);
            handleClinicalStudy("protozoa", false);
        }
    }, [selectAll]);

    const handleExtraAnalysis = (field, value) => {
        let obj = clinicalStudy.otherAnalysis
            ? clinicalStudy.otherAnalysis.slice()
            : [];
        obj.push(value);
        setClinicalStudy((prev) => ({ ...prev, otherAnalysis: obj }));
    };

    const removeAnalysis = (indx) => {
        let auxStudies = clinicalStudy.otherAnalysis.slice(0);
        auxStudies.splice(indx, 1);
        setClinicalStudy((prev) => ({ ...prev, otherAnalysis: auxStudies }));
    };

    useEffect(() => {
        if (props.consultation.clinicalStudy && !firstRender) {
            setClinicalStudy(props.consultation.clinicalStudy);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, firstRender]);

    useEffect(() => {
        props.handleChange("clinicalStudy", clinicalStudy);
    }, [clinicalStudy]);

    const downloadPDF = function () {
        setLoadingDownload(true);
        setDownloadError("");
        api.clinicalStudy
            .get({
                patient: props.consultation.patient,
                doctor: props.consultation.doctor,
                generalObservations:
                    props.consultation.clinicalStudy.generalObservations,
                clinicalStudy: props.consultation.clinicalStudy,
                printWithInsurance: props.printWithInsurance,
            })
            .then(
                (res) => {
                    setLoadingDownload(false);
                    const url = window.URL.createObjectURL(new Blob([res]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "EstudiosClinicos.pdf");
                    document.body.appendChild(link);
                    link.click();
                },
                async (err) => {
                    setDownloadError(
                        JSON.parse(await err.response.data.text()).detail,
                    );
                    setLoadingDownload(false);
                },
            );
    };

    return (
        <div>
            <div className="space-y-5 px-4 py-6 sm:p-4">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                    <TabSection
                        title="Analisis clinicos"
                    >
                        <div className="flex flex-col gap-6">
                        <div>
                            <span
                                onClick={() => setSelectAll(!selectAll)}
                                className="mt-2 text-primary underline cursor-pointer"
                            >
                                {selectAll
                                    ? "Deseleccionar todos"
                                    : "Seleccionar todos"}
                            </span>
                            <div className="mt-4 grid grid-cols-2 gap-y-4">
                                <InlineCheckbox
                                    value={clinicalStudy?.hemogram}
                                    handleChange={handleClinicalStudy}
                                    name="hemogram"
                                    label="Hemograma"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.vdrl}
                                    handleChange={handleClinicalStudy}
                                    name="vdrl"
                                    label="VDRL"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.glycemia}
                                    handleChange={handleClinicalStudy}
                                    name="glycemia"
                                    label="Glicemia"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.bloodCrasis}
                                    handleChange={handleClinicalStudy}
                                    name="bloodCrasis"
                                    label="Crasis sanguínea"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.urea}
                                    handleChange={handleClinicalStudy}
                                    name="urea"
                                    label="Urea"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.urineAnalysis}
                                    handleChange={handleClinicalStudy}
                                    name="urineAnalysis"
                                    label="Orina simple"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.creatinine}
                                    handleChange={handleClinicalStudy}
                                    name="creatinine"
                                    label="Creatinina"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.stool}
                                    handleChange={handleClinicalStudy}
                                    name="stool"
                                    label="Heces"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.lipidProfile}
                                    handleChange={handleClinicalStudy}
                                    name="lipidProfile"
                                    label="Perfil lípido"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.worms}
                                    handleChange={handleClinicalStudy}
                                    name="worms"
                                    label="Vermes"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.hiv}
                                    handleChange={handleClinicalStudy}
                                    name="hiv"
                                    label="VIH"
                                />
                                <InlineCheckbox
                                    value={clinicalStudy?.protozoa}
                                    handleChange={handleClinicalStudy}
                                    name="protozoa"
                                    label="Protozoarios"
                                />
                                <AddInput
                                    unit="Análisis"
                                    value={clinicalStudy?.otherAnalysis}
                                    handleDelete={removeAnalysis}
                                    handleChange={handleExtraAnalysis}
                                    name="otherAnalysis"
                                    label="Otros"
                                    className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 shadow-sm focus:border-primary focus:ring-primary dark:border-slate-700 dark:bg-slate-950"
                                />
                            </div>
                        </div>
                        <TextEditor
                            value={clinicalStudy?.generalObservations}
                            handleChange={handleClinicalStudy}
                            name="generalObservations"
                            label="Observaciones generales"
                        />
                        </div>
                    </TabSection>
                    <TabSection
                        title="Electrocardiograma"
                    >
                        <div className="flex flex-col gap-3 p-1 dark:text-white">
                        <p>Electrocardiograma</p>
                        <div className="flex flex-row gap-3 text-sm font-medium text-gray-700 dark:text-gray-400">
                            <p>Edad</p>
                            {props.age ? `${props.age} años` : "---"}
                        </div>
                        <div className="flex gap-3">
                            <p>DX.</p>
                            <p>Prequirurgico</p>
                        </div>
                        <InlineCheckbox
                            value={clinicalStudy?.noEcgTest}
                            handleChange={handleClinicalStudy}
                            name="noEcgTest"
                            label="No fue necesaria una prueba de electrocardiograma"
                        />
                        </div>
                    </TabSection>
                </div>
            </div>
            <TabFooter
                leftAction={
                    <button
                        onClick={() => props.setTab("Estudios oftalmológicos")}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:text-primary dark:bg-gray-900"
                    >
                        Anterior
                    </button>
                }
                rightAction={
                    <>
                        {downloadError && (
                            <p className="max-w-xs text-sm font-medium text-[#F51515]">
                                {downloadError}
                            </p>
                        )}
                        <div className="flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-black shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                            <Toggle
                                value={props.printWithInsurance}
                                handleChange={props.setPrintWithInsurance}
                            />
                            <p className="text-sm font-medium">
                                Imprimir con seguro
                            </p>
                        </div>
                        {loadingDownload ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary" />
                        ) : (
                            <button
                                onClick={() => downloadPDF()}
                                type="button"
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                <PdfIcon />
                                <span>Imprimir receta</span>
                            </button>
                        )}
                        <button
                            onClick={() => props.setTab('Cirugía')}
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                        >
                            Siguiente
                        </button>
                    </>
                }
            />
        </div>
    );
};

const Surgery = function (props) {
    const [surgeryOrder, setSurgeryOrder] = useState({});
    const [firstRender, setFirstRender] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadError, setDownloadError] = useState(undefined);
    const [procedures, setProcedures] = useState([]);
    const [procedureCreateError, setProcedureCreateError] = useState("");

    const handleSurgery = (field, value) =>
        setSurgeryOrder((prev) => ({ ...prev, [field]: value }));

    const handleChange = (field, value) =>
        setSurgeryOrder((prev) => ({ ...prev, [field]: value }));

    useEffect(() => {
        api.surgeries.getMulti().then((response) => {
            setProcedures(response);
        });
    }, []);

    useEffect(() => {
        if (props.consultation.surgeryOrder && !firstRender) {
            setSurgeryOrder(props.consultation.surgeryOrder);
            setFirstRender(true);
        } else if (props.consultation && !firstRender) {
            setFirstRender(true);
        }
    }, [props.consultation, firstRender]);

    useEffect(() => {
        props.handleChange("surgeryOrder", surgeryOrder);
    }, [surgeryOrder]);

    const createProcedure = async (name) => {
        try {
            setProcedureCreateError("");
            let response = await api.surgeries.create(name);
            setProcedures((prev) => [...prev, response]);
            return response;
        } catch (e) {
            const msg =
                e?.message?.name?.[0] ||
                e?.message?.detail ||
                "No se pudo crear el procedimiento";
            setProcedureCreateError(msg);
            return Promise.reject(e);
        }
    };

    const downloadPDF = function () {
        setLoadingDownload(true);
        setDownloadError("");

        const surgeryIds = (surgeryOrder?.surgeries || [])
            .map((s) => {
                if (typeof s === "number") return s;
                if (typeof s === "object" && s !== null && s.id) return s.id;
                return null;
            })
            .filter((id) => id !== null);

        api.surgeryOrder
            .get({
                patient: props.consultation.patient,
                doctor: props.consultation.doctor,
                surgeryOrder: {
                    surgeryIds: surgeryIds,
                    generalObservations: surgeryOrder?.generalObservations || "",
                },
                printWithInsurance: props.printWithInsurance,
            })
            .then(
                (res) => {
                    setLoadingDownload(false);
                    const url = window.URL.createObjectURL(new Blob([res]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "OrdenCirugia.pdf");
                    document.body.appendChild(link);
                    link.click();
                },
                async (err) => {
                    setDownloadError(
                        JSON.parse(await err.response.data.text()).detail,
                    );
                    setLoadingDownload(false);
                },
            );
    };

    return (
        <div>
            <div className="space-y-5 px-4 py-6 sm:p-4">
                <TabSection
                    title="Cirugia"
                >
                    <div className="flex gap-3">
                        <CustomCombobox
                            create={createProcedure}
                            multiple={true}
                            value={surgeryOrder?.surgeries}
                            error={
                                procedureCreateError || props.errors?.surgeries
                            }
                            handleChange={handleSurgery}
                            options={procedures}
                            name="surgeries"
                            containerClassName="col-span-1 w-full relative"
                            label="Buscar tipo de cirugía"
                        />
                    </div>
                    <TextEditor
                        error={props.errors?.generalObservations}
                        value={surgeryOrder?.generalObservations}
                        handleChange={handleChange}
                        name="generalObservations"
                        label="Observaciones generales"
                    />
                </TabSection>
            </div>
            <TabFooter
                leftAction={
                    <button
                        onClick={() => props.setTab("Estudios clínicos")}
                        type="button"
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm hover:text-primary dark:bg-gray-900"
                    >
                        Anterior
                    </button>
                }
                rightAction={
                    <>
                        {downloadError && (
                            <p className="max-w-xs text-sm font-medium text-[#F51515]">
                                {downloadError}
                            </p>
                        )}
                        <div className="flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-black shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                            <Toggle
                                value={props.printWithInsurance}
                                handleChange={props.setPrintWithInsurance}
                            />
                            <p className="text-sm font-medium">
                                Imprimir con seguro
                            </p>
                        </div>
                        {loadingDownload ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary" />
                        ) : (
                            <button
                                onClick={() => downloadPDF()}
                                type="button"
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                            >
                                <PdfIcon />
                                <span>Imprimir receta</span>
                            </button>
                        )}
                        <button
                            onClick={(event) => props.handleSubmit(event)}
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary"
                        >
                            {props.loadingSubmit ? (
                                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
                            ) : (
                                "Guardar"
                            )}
                        </button>
                    </>
                }
            />
        </div>
    );
};

// const Ecobiometry = function (props) {
//     const [ecobiometry, setEcobiometry] = useState({});
//     const [firstRender, setFirstRender] = useState(false);

//     const handleEcobiometry = (field, value) =>
//         setEcobiometry((prev) => ({ ...prev, [field]: value }));

//     useEffect(() => {
//         if (props.consultation.ecobiometry && !firstRender) {
//             setEcobiometry(props.consultation.ecobiometry);
//             setFirstRender(true);
//         } else if (props.consultation && !firstRender) {
//             setFirstRender(true);
//         }
//     }, [props.consultation, firstRender]);

//     useEffect(() => {
//         props.handleChange("ecobiometry", {
//             ...ecobiometry,
//             doctor: props.consultation.doctor,
//             patient: props.consultation.patient,
//         });
//     }, [ecobiometry]);

//     return (
//         <div>
//             <div className="px-4 py-6 sm:p-4 dark:text-white overflow-auto">
//                 <div className="flex flex-col  min-w-[500px]">
//                     <div className="flex flex-row gap-6">
//                         <p className="my-auto font-semibold text-lg">
//                             Instrumentación
//                         </p>
//                         <TextInput
//                             containerClassName="w-1/3"
//                             value={ecobiometry?.instrumentation}
//                             handleChange={handleEcobiometry}
//                             name="instrumentation"
//                         />
//                     </div>
//                     <div className="grid grid-cols-3">
//                         <div />
//                         <div className="grid grid-cols-2 col-span-2 divide-x-4">
//                             <p className="w-full text-center mx-auto font-semibold pr-12">
//                                 OI
//                             </p>
//                             <p className="w-full text-center mx-auto font-semibold pl-12">
//                                 OD
//                             </p>
//                         </div>
//                         <p className="my-auto font-semibold text-lg">
//                             Longitud axial
//                         </p>
//                         <div className="grid grid-cols-2 col-span-2 divide-x-4">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.axialLengthLeft}
//                                 handleChange={handleEcobiometry}
//                                 name="axialLengthLeft"
//                                 containerClassName="h-fit pr-12"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.axialLengthRight}
//                                 handleChange={handleEcobiometry}
//                                 name="axialLengthRight"
//                                 containerClassName="h-fit pl-12"
//                             />
//                         </div>
//                         <p className="my-auto font-semibold text-lg">
//                             Queratometría
//                         </p>
//                         <div className="grid grid-cols-2 col-span-2 divide-x-4">
//                             <div className="grid grid-cols-2 py-4 gap-6 pr-6">
//                                 <div>
//                                     <div className="flex gap-6">
//                                         <p className="my-auto">K1</p>
//                                         <TextInput
//                                             onKeyDown={(event) => {
//                                                 if (
//                                                     /[\d,.]/.test(event.key) ||
//                                                     event.key === "Backspace" ||
//                                                     event.key === "Tab" ||
//                                                     event.key === "ArrowLeft" ||
//                                                     event.key ===
//                                                         "ArrowRight" ||
//                                                     event.key === "ArrowUp" ||
//                                                     event.key === "ArrowDown"
//                                                 ) {
//                                                 } else {
//                                                     event.preventDefault();
//                                                 }
//                                             }}
//                                             value={ecobiometry?.k1Left}
//                                             handleChange={handleEcobiometry}
//                                             name="k1Left"
//                                         />
//                                     </div>
//                                     <div className="flex gap-6">
//                                         <p className="my-auto">K2</p>
//                                         <TextInput
//                                             onKeyDown={(event) => {
//                                                 if (
//                                                     /[\d,.]/.test(event.key) ||
//                                                     event.key === "Backspace" ||
//                                                     event.key === "Tab" ||
//                                                     event.key === "ArrowLeft" ||
//                                                     event.key ===
//                                                         "ArrowRight" ||
//                                                     event.key === "ArrowUp" ||
//                                                     event.key === "ArrowDown"
//                                                 ) {
//                                                 } else {
//                                                     event.preventDefault();
//                                                 }
//                                             }}
//                                             value={ecobiometry?.k2Left}
//                                             handleChange={handleEcobiometry}
//                                             name="k2Left"
//                                         />
//                                     </div>
//                                 </div>
//                                 <TextArea
//                                     handleChange={handleEcobiometry}
//                                     name="keratometryLeftDescription"
//                                 />
//                             </div>
//                             <div className="grid grid-cols-2 py-4 gap-6 pl-6">
//                                 <div>
//                                     <div className="flex gap-6">
//                                         <p className="my-auto">K1</p>
//                                         <TextInput
//                                             onKeyDown={(event) => {
//                                                 if (
//                                                     /[\d,.]/.test(event.key) ||
//                                                     event.key === "Backspace" ||
//                                                     event.key === "Tab" ||
//                                                     event.key === "ArrowLeft" ||
//                                                     event.key ===
//                                                         "ArrowRight" ||
//                                                     event.key === "ArrowUp" ||
//                                                     event.key === "ArrowDown"
//                                                 ) {
//                                                 } else {
//                                                     event.preventDefault();
//                                                 }
//                                             }}
//                                             value={ecobiometry?.k1Right}
//                                             handleChange={handleEcobiometry}
//                                             name="k1Right"
//                                         />
//                                     </div>
//                                     <div className="flex gap-6">
//                                         <p className="my-auto">K2</p>
//                                         <TextInput
//                                             onKeyDown={(event) => {
//                                                 if (
//                                                     /[\d,.]/.test(event.key) ||
//                                                     event.key === "Backspace" ||
//                                                     event.key === "Tab" ||
//                                                     event.key === "ArrowLeft" ||
//                                                     event.key ===
//                                                         "ArrowRight" ||
//                                                     event.key === "ArrowUp" ||
//                                                     event.key === "ArrowDown"
//                                                 ) {
//                                                 } else {
//                                                     event.preventDefault();
//                                                 }
//                                             }}
//                                             value={ecobiometry?.k2Right}
//                                             handleChange={handleEcobiometry}
//                                             name="k2Right"
//                                         />
//                                     </div>
//                                 </div>
//                                 <TextArea
//                                     handleChange={handleEcobiometry}
//                                     name="keratometryRightDescription"
//                                 />
//                             </div>
//                         </div>
//                         <p className="my-auto font-semibold text-lg">
//                             Profundidad de CA
//                         </p>
//                         <div className="grid grid-cols-2 col-span-2 divide-x-4">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 containerClassName="w-full pr-6 pt-4"
//                                 value={ecobiometry?.anteriorChamberDepthLeft}
//                                 handleChange={handleEcobiometry}
//                                 name="anteriorChamberDepthLeft"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 containerClassName="w-full pl-6 pt-4"
//                                 value={ecobiometry?.anteriorChamberDepthRight}
//                                 handleChange={handleEcobiometry}
//                                 name="anteriorChamberDepthRight"
//                             />
//                         </div>
//                         <p className="my-auto font-semibold text-lg">
//                             Fórmula utilizada
//                         </p>
//                         <div className="grid grid-cols-2 col-span-2 divide-x-4">
//                             <TextInput
//                                 containerClassName="w-full pr-6 pt-4"
//                                 value={ecobiometry?.formulaUsedLeft}
//                                 handleChange={handleEcobiometry}
//                                 name="formulaUsedLeft"
//                             />
//                             <TextInput
//                                 containerClassName="w-full pl-6 pt-4"
//                                 value={ecobiometry?.formulaUsedRight}
//                                 handleChange={handleEcobiometry}
//                                 name="formulaUsedRight"
//                             />
//                         </div>
//                     </div>
//                     <p className="font-semibold text-lg mt-6">
//                         Resultados para emetropía
//                     </p>
//                     <div className="grid grid-cols-3 gap-x-6">
//                         <div />
//                         <p className="mx-auto font-semibold">OI</p>
//                         <p className="mx-auto font-semibold">OD</p>
//                         <div />
//                         <div className="grid grid-cols-2 text-center font-semibold gap-6">
//                             <p>Constante</p>
//                             <p>Dioptría</p>
//                         </div>
//                         <div className="grid grid-cols-2 text-center font-semibold gap-6">
//                             <p>Constante</p>
//                             <p>Dioptría</p>
//                         </div>
//                         <p className="my-auto font-semibold">
//                             Lente intraocular de cámara anterior
//                         </p>
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensAnteriorLeftConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensAnteriorLeftConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.intraocularLensAnteriorLeft}
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensAnteriorLeft"
//                             />
//                         </div>
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensAnteriorRightConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensAnteriorRightConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensAnteriorRight
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensAnteriorRight"
//                             />
//                         </div>
//                         <p className="my-auto font-semibold">
//                             Lente intraocular de cámara posterior
//                         </p>
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensPosteriorLeftConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensPosteriorLeftConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensPosteriorLeft
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensPosteriorLeft"
//                             />
//                         </div>
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensPosteriorRightConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensPosteriorRightConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.intraocularLensPosteriorRight
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="intraocularLensPosteriorRight"
//                             />
//                         </div>
//                         <div />
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.emmetropyAdditional1LeftConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional1LeftConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.emmetropyAdditional1Left}
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional1Left"
//                             />
//                         </div>
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.emmetropyAdditional1RightConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional1RightConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.emmetropyAdditional1Right}
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional1Right"
//                             />
//                         </div>
//                         <div />
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.emmetropyAdditional2LeftConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional2LeftConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.emmetropyAdditional2Left}
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional2Left"
//                             />
//                         </div>
//                         <div className="grid grid-cols-2 gap-3">
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={
//                                     ecobiometry?.emmetropyAdditional2RightConstant
//                                 }
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional2RightConstant"
//                             />
//                             <TextInput
//                                 onKeyDown={(event) => {
//                                     if (
//                                         /[\d,.]/.test(event.key) ||
//                                         event.key === "Backspace" ||
//                                         event.key === "Tab" ||
//                                         event.key === "ArrowLeft" ||
//                                         event.key === "ArrowRight" ||
//                                         event.key === "ArrowUp" ||
//                                         event.key === "ArrowDown"
//                                     ) {
//                                     } else {
//                                         event.preventDefault();
//                                     }
//                                 }}
//                                 value={ecobiometry?.emmetropyAdditional2Right}
//                                 handleChange={handleEcobiometry}
//                                 name="emmetropyAdditional2Right"
//                             />
//                         </div>
//                     </div>
//                 </div>
//             </div>
//             <div className="px-4 py-4 sm:px-6 bg-gray-100 dark:bg-gray-800">
//                 <div className="flex justify-between">
//                     <button
//                         onClick={() => props.setTab("Cirugía")}
//                         type="button"
//                         className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm bg-white dark:bg-gray-900 text-primary hover:text-primary"
//                     >
//                         Anterior
//                     </button>
//                     <div className="flex">
//                         <button
//                             onClick={(event) => props.handleSubmit(event)}
//                             type="button"
//                             className="ml-10 inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
//                         >
//                             {props.loadingSubmit ? (
//                                 <div className="w-5 h-5 mx-5 border-b-2 border-white rounded-full animate-spin"></div>
//                             ) : (
//                                 "Guardar"
//                             )}
//                         </button>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

export default function FormNewConsultation(props) {
    return (
        <div>
            {props.tab === "General" && (
                <General
                    consultation={props.consultation}
                    handleChange={props.handleChange}
                    setTab={props.changeTab}
                    errors={props.errors?.general}
                    handleError={props.handleError}
                    idConsultation={props.idConsultation}
                    history={props.history}
                />
            )}
            {props.tab === "Visión y Tensión" && (
                <VisionTension
                    consultation={props.consultation}
                    handleChange={props.handleChange}
                    setTab={props.changeTab}
                    errors={props.errors?.visionTension}
                    errorTensionRight={props.errors?.tensionRight}
                    errorTensionLeft={props.errors?.tensionLeft}
                    errorSampleTime={props.errors?.sampleTime}
                    visionFieldsErrors={props.errors?.visionFields}
                    handleError={props.handleError}
                    idConsultation={props.idConsultation}
                />
            )}
            {props.tab === "Refracción" && (
                <Refraction
                    consultation={props.consultation}
                    handleChange={props.handleChange}
                    errors={props.errors?.refraction}
                    setTab={props.changeTab}
                    refractionRecords={props.refractionRecords}
                    printWithInsurance={props.printWithInsurance}
                    setPrintWithInsurance={props.setPrintWithInsurance}
                />
            )}
            {props.tab === "Medicación" && (
                <Medications
                    consultation={props.consultation}
                    medicines={props.medicines}
                    handleChange={props.handleChange}
                    setTab={props.changeTab}
                    addMedication={props.addMedication}
                    removeMedication={props.removeMedication}
                    handleMedication={props.handleMedication}
                    errors={props.errors?.medications}
                    medicationsRecords={props.medicationsRecords}
                    printWithInsurance={props.printWithInsurance}
                    setPrintWithInsurance={props.setPrintWithInsurance}
                />
            )}
            {props.tab === "Estudios oftalmológicos" && (
                <OphthalmologicalStudies
                    consultation={props.consultation}
                    handleChange={props.handleChange}
                    setTab={props.changeTab}
                    errors={props.errors?.ophthalmologicalStudy}
                    printWithInsurance={props.printWithInsurance}
                    setPrintWithInsurance={props.setPrintWithInsurance}
                />
            )}
            {props.tab === "Estudios clínicos" && (
                <ClinicalStudies
                    consultation={props.consultation}
                    handleChange={props.handleChange}
                    setTab={props.changeTab}
                    errors={props.errors?.clinicalStudy}
                    handleSubmit={props.handleSubmit}
                    age={props.age}
                    printWithInsurance={props.printWithInsurance}
                    setPrintWithInsurance={props.setPrintWithInsurance}
                />
            )}
            {props.tab === "Cirugía" && (
                <Surgery
                    consultation={props.consultation}
                    handleChange={props.handleChange}
                    surgeries={props.surgeries}
                    doctors={props.doctors}
                    userId={props.userId}
                    setTab={props.changeTab}
                    errors={props.errors?.surgery}
                    errorEcg={props.errors?.ecg}
                    handleSubmit={props.handleSubmit}
                    loadingSubmit={props.loadingSubmit}
                />
            )}
            {/* {props.tab === 'Ecobiometría' && <Ecobiometry
                consultation={props.consultation}
                handleChange={props.handleChange}
                setTab={props.changeTab}
                errors={props.errors?.ecobiometry}
                handleSubmit={props.handleSubmit}
                loadingSubmit={props.loadingSubmit}
            />} */}
        </div>
    );
}
